import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

// ==========================================================
// Babilonia + NR / Procotiza - Flujo unificado asesor-cliente
// ----------------------------------------------------------
// Propuesta independiente para consumir Procotiza NR.
// Los datos auxiliares del cliente solo viven en el estado de React;
// únicamente se envía a NR lo necesario para cotizar, guardar en NR y generar PDF.
// El guardado usa la clave de agencia NRFM y la estructura validada en UAT.
// /cotiza siempre se ejecuta con IdCotizacion=0 para calcular una operación nueva.
// Los resultados muestran por separado el enganche enviado y el regresado por NR.
// Los años se consultan a NR sin filtros.
// idInsurance solo se llena cuando existe una opción válida regresada por Anzen.
// ==========================================================

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://api.checkmark.mx/";

// -----------------------------------------------------------------
// Únicas dos constantes "default" del flujo: la clave y el ID de
// agencia de pruebas que NR entregó (NRFM / 101). Todo lo demás debe
// salir de una respuesta real de NR o de una selección explícita del
// asesor; si no hay dato, el campo se queda vacío y la UI/validación
// deben decir "no disponible" en vez de inventar un valor.
// -----------------------------------------------------------------
const NR_DEFAULT_AGENCIA_CLAVE =
  process.env.REACT_APP_NR_AGENCIA_CLAVE || "NRFM";
const NR_DEFAULT_AGENCIA_ID = Number(
  process.env.REACT_APP_NR_AGENCIA_ID || 101,
);

// AnzenId es un identificador de configuración de la integración (a nivel
// tienda/ambiente), no un dato por cotización ni un catálogo consultable.
// Se mantiene como configuración de entorno igual que la agencia, porque
// no existe endpoint NR del que se pueda leer/derivar dinámicamente.
// Si NR llegara a exponerlo por catálogo, debe moverse fuera de aquí.
const NR_ANZEN_ID = process.env.REACT_APP_NR_ANZEN_ID || "AZ001";

const NR_INCLUDE_ANZEN_DATA_ON_SAVE =
  String(process.env.REACT_APP_NR_INCLUDE_ANZEN_DATA_ON_SAVE || "false").toLowerCase() ===
  "true";

// ==========================================================
// PENDIENTE — Anzen.GetQuotes.AccesoFalse (IdPaqueteExterno)
// ----------------------------------------------------------
// GetCatalogsAnzen funciona con PlanId=idPaquete interno (58846) sin
// problema, pero consultar-seguros-anzen (GetQuotes) da AccesoFalse con
// ese mismo número en IdPaqueteExterno. Ningún catálogo documentado trae
// el valor correcto (/catalogs/paquetes.idExterno resultó ser el código
// del documento impreso, no un ID de Anzen).
//
// Confirmado con una prueba puntual: forzar IdPaqueteExterno=58144 sí
// destraba Anzen, pero SOLO para el vehículo con el que se probó — es
// decir, el valor correcto depende del vehículo/producto, no solo del
// paquete. No se hardcodea ningún mapeo aquí porque no es sostenible
// mantenerlo a mano por vehículo en el frontend: hace falta que NR
// confirme de qué servicio/catálogo sale este dato (o, si es una tabla
// fija que ellos mantienen, debe vivir en la base de datos del backend,
// no en el código de React). Mientras tanto, IdPaqueteExterno se sigue
// calculando normal con resolvePackageExternalId, y Anzen seguirá
// fallando con AccesoFalse hasta tener esa respuesta.

const NR_ENDPOINTS = {
  cotiza: "/financiamiento/nr/cotiza",
  guardarCotizacion: "/financiamiento/nr/guardar-cotizacion",
  generarPdf: "/financiamiento/nr/generar-pdf",
  paquetes: "/financiamiento/nr/paquetes",
  marcas: "/financiamiento/nr/catalogos/marcas",
  modelos: "/financiamiento/nr/catalogos/modelos",
  versiones: "/financiamiento/nr/catalogos/versiones",
  estrategias: "/financiamiento/nr/catalogos/estrategias",
  clasificaciones: "/financiamiento/nr/catalogos/clasificaciones",
  anios: "/financiamiento/nr/catalogos/anios",
  productos: "/financiamiento/nr/catalogos/productos",
  planes: "/financiamiento/nr/catalogos/planes",
  plazos: "/financiamiento/nr/catalogos/plazos",
  tiposSeguro: "/financiamiento/nr/catalogos/tipos-seguro",
  segurosVida: "/financiamiento/nr/catalogos/seguros-vida",
  estados: "/financiamiento/nr/catalogos/estados",
  anzenCatalogs: "/financiamiento/nr/catalogos/anzen",
  productoFiltrado: "/financiamiento/nr/producto-filtrado",
  consultarSegurosAnzen: "/financiamiento/nr/consultar-seguros-anzen",
};

// El catálogo de años se obtiene exclusivamente desde NR mediante
// /financiamiento/nr/catalogos/anios, enviando un body vacío y sin filtros.

const EMPTY_CATALOGS = {
  clasificaciones: [],
  estados: [],
  marcas: [],
  modelos: [],
  anios: [],
  versiones: [],
  estrategias: [],
  productos: [],
  productosFiltrados: [],
  planes: [],
  paquetes: [],
  plazos: [],
  tiposSeguro: [],
  segurosVida: [],
  anzen: {},
};

const EMPTY_CLIENT = {
  nombre: "",
  telefono: "",
  correo_electronico: "",
  fuente: "Punto de venta",
  asesor: "",
  user_id: "",
};

const EMPTY_QUOTE = {
  venta_id: "",
  folio_venta: "",

  // Compatibilidad Babilonia. Se mantiene el equivalente a depende_auto:
  // "Si" = Contado, "No" = Crédito.
  depende_auto: "No",

  agenciaClave: NR_DEFAULT_AGENCIA_CLAVE,
  agenciaId: String(NR_DEFAULT_AGENCIA_ID),
  clasificacionId: "63",
  clasificacionNombre: "Nuevo",

  personalidadJuridica: "1", // 1=PF, 2=PM para guardar cotización.
  idPerJuridica: "14", // 14=PF, 15=PM para /cotiza.
  fiscalPersonalityId: "PF001", // para Anzen.

  tipoOperacionId: "1", // 1=Crédito, 2=Leasing, 3=Balloon.

  estadoId: "",
  estadoNombre: "",
  municipioId: "",
  municipioNombre: "",

  marcaId: "",
  marcaNombre: "",
  modeloId: "",
  modeloNombre: "",
  anio: "",
  versionId: "",
  versionNombre: "",
  estrategiaId: "",
  estrategiaNombre: "",
  productoId: "",
  productoNombre: "",
  // Debe venir del catálogo real de NR (idTipoPrecio/tipoDePrecio/tipoCxa del
  // producto). Si NR no lo trae, se queda vacío y guardar-cotizacion debe
  // rechazar la operación en vez de mandar un valor inventado.
  productoTipoPrecioId: "",
  anzenProductoId: "",
  // Debe venir del catálogo real de NR (idTipoProducto del producto). Vacío
  // si NR no lo trae; el flujo de Anzen debe bloquear hasta que exista.
  idTipoProducto: "",
  idBroker: "0",
  planId: "",
  planNombre: "",
  paqueteExternoId: "",
  plazo: "",

  precioVehiculo: "",
  enganche: "",
  porcentajeEnganche: "20",
  // 0 indica a Procotiza que debe usar la tasa configurada en el paquete.
  tasaEditadaNR: "0",

  accesoriosFinanciados: "0",
  accesoriosContado: "0",
  garantiaExtendida: "0",
  productosAdicionales: "0",

  tipoSeguroId: "32",
  tipoSeguroNombre: "",
  // Selección real del catálogo /catalogs/seguros-vida (ids propios de NR,
  // ej. 1=Seguro Temporal, 2=Seguro Vitalicio). NO es el mismo valor que
  // TipoSeguroVida de /cotiza (0=ninguno, 227=solo vida): ese enum se deriva
  // de si aquí hay o no selección, no se guarda directamente.
  seguroVidaId: "",
  aseguradoraId: "",
  aseguradoraNombre: "",
  coberturaId: "",
  coberturaNombre: "",
  // Catálogo Anzen (TypeOfUsesId, vía GetCatalogsAnzen -> TypeOfUses).
  // Solo se usa para consultar-seguros-anzen; no confundir con el campo
  // fijo "Uso" que espera /cotiza y /quotations (ver NR_USO_FIJO).
  tipoUsoId: "",
  tipoUsoNombre: "",
  udiId: "0",
  udiNombre: "",
  tipoCompraSeguro: "Financiado",
  montoSeguro: "0",
  gpsId: "0",

  seguroAnzenKey: "",
  seguroAnzenId: "",
  seguroAnzenCampaniaId: "",
  seguroAnzenAmount: "0",
  seguroAnzenTotalAmount: "0",
  seguroAnzenSurcharges: "0",
  seguroAnzenTipo: "",

  // Procotiza calcula la comisión por apertura al cotizar.
  comisionApertura: "0",

  anzenId: NR_ANZEN_ID,
};

// El catálogo /catalogs/clasificaciones de NR siempre regresa exactamente
// estos dos valores fijos (63=Nuevos, 64=Seminuevos, sin filtros). No es un
// "default adivinado": es la respuesta documentada y estable de NR, por lo
// que clasificacionId puede preseleccionarse como punto de partida de UX.
// Si la consulta real a NR falla, catalogs.clasificaciones se deja vacío
// (ver loadBaseCatalogs) y el selector muestra "Sin opciones disponibles".

const PERSONALIDADES = [
  { id: "1", cotizaId: "14", fiscalId: "PF001", nombre: "Persona Física" },
  { id: "2", cotizaId: "15", fiscalId: "PM001", nombre: "Persona Moral" },
];

const TIPOS_OPERACION = [
  { id: "1", nombre: "Crédito" },
  { id: "2", nombre: "Leasing" },
  { id: "3", nombre: "Balloon" },
];

const formatCurrency = (value) => {
  const numeric = toNumber(value);
  return numeric.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined) return 0;
  const clean = String(value).replace(/[$,\s]/g, "").replace(/[^0-9.-]/g, "");
  const parsed = Number(clean);
  return Number.isFinite(parsed) ? parsed : 0;
}

function safeString(value) {
  return String(value ?? "").trim();
}

function nrAsArray(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  if (Array.isArray(value.data)) return value.data;
  if (Array.isArray(value.Data)) return value.Data;
  if (Array.isArray(value.resultados)) return value.resultados;
  if (Array.isArray(value.Resultados)) return value.Resultados;
  if (Array.isArray(value.items)) return value.items;
  if (Array.isArray(value.Items)) return value.Items;
  if (Array.isArray(value.result)) return value.result;
  if (Array.isArray(value.Result)) return value.Result;
  if (Array.isArray(value.opciones)) return value.opciones;
  if (Array.isArray(value.Opciones)) return value.Opciones;

  // Algunos catálogos pueden regresar un objeto contenedor con una sola propiedad arreglo.
  for (const item of Object.values(value)) {
    if (Array.isArray(item)) return item;
  }

  return [];
}

function nrFirstObject(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] || null;
  if (typeof value === "object") {
    const nested = nrAsArray(value);
    return nested[0] || value;
  }
  return null;
}

function nrCatalogItems(value) {
  const items = nrAsArray(value);
  if (items.length) return items;
  if (value && typeof value === "object" && !Array.isArray(value)) return [value];
  return [];
}

function normalizeNrYears(value) {
  const years = nrCatalogItems(value)
    .map((item) => {
      if (typeof item === "number" || typeof item === "string") {
        const year = Number(item);

        return Number.isFinite(year) && year > 0
          ? {
              id: year,
              anio: year,
              nombre: String(year),
            }
          : null;
      }

      const rawYear = nrGetValue(item, [
        "anio",
        "Anio",
        "año",
        "Año",
        "anioModelo",
        "AnioModelo",
        "idAnio",
        "IdAnio",
        "valor",
        "Valor",
        "id",
        "Id",
      ]);

      const year = Number(rawYear);

      if (!Number.isFinite(year) || year <= 0) {
        return null;
      }

      return {
        ...item,
        id: year,
        anio: year,
        nombre: String(year),
      };
    })
    .filter(Boolean);

  return years
    .filter(
      (item, index, items) =>
        items.findIndex(
          (candidate) => Number(candidate.anio) === Number(item.anio),
        ) === index,
    )
    .sort((first, second) => Number(second.anio) - Number(first.anio));
}

function dedupeNrProducts(value) {
  const validProducts = nrCatalogItems(value).filter((item) => {
    const productId = safeString(
      nrGetValue(item, [
        "idProducto",
        "IdProducto",
        "productoId",
        "ProductoId",
        "id",
        "Id",
      ]),
    );

    // Evita mostrar opciones vacías o productos sin un identificador utilizable.
    return Boolean(productId && productId !== "0");
  });

  // Sin producto de respaldo: si NR no regresó nada válido, la lista se
  // queda vacía y la UI/validación deben decirlo tal cual ("no disponible"),
  // no inventar un producto de prueba.
  return validProducts.filter((item, index, items) => {
    const productId = safeString(nrGetId(item, ["idProducto", "IdProducto"]));
    return (
      items.findIndex(
        (candidate) =>
          safeString(nrGetId(candidate, ["idProducto", "IdProducto"])) === productId,
      ) === index
    );
  });
}

function getDefaultCatalogItem(options = []) {
  const items = nrCatalogItems(options);
  return (
    items.find((item) => {
      const defaultValue = nrGetValue(item, [
        "isDefault",
        "IsDefault",
        "regDefault",
        "RegDefault",
      ]);
      return defaultValue === true || Number(defaultValue) === 1;
    }) ||
    (items.length === 1 ? items[0] : null) ||
    null
  );
}

function getProductPrice(product, clasificacionId = "63") {
  if (!product || typeof product !== "object") return "";

  const isSeminuevo = String(clasificacionId) === "64";
  const keys = isSeminuevo
    ? [
        "precioSeminuevo",
        "PrecioSeminuevo",
        "precio",
        "Precio",
        "precioProducto",
        "PrecioProducto",
        "precioLista",
        "PrecioLista",
      ]
    : [
        "precio",
        "Precio",
        "precioProducto",
        "PrecioProducto",
        "precioLista",
        "PrecioLista",
        "precioSeminuevo",
        "PrecioSeminuevo",
      ];

  return safeString(nrGetValue(product, keys));
}

// /catalogs/productos y /catalogs/producto-filtrado usan espacios de
// idProducto DISTINTOS para el mismo vehículo real (confirmado con datos
// reales: mismo MAGNITE ADVANCE CVT 25, idProducto 29147 en uno y 28773
// en el otro). Emparejar por idProducto entre los dos catálogos falla
// siempre en silencio. idVersion sí es consistente entre ambos catálogos,
// así que es la llave correcta para "enriquecer" un producto de
// /catalogs/productos con los datos de Anzen (idExterno) que solo trae
// /catalogs/producto-filtrado.
function findProductInFiltradoByVersion(productosFiltrados, product) {
  const items = nrCatalogItems(productosFiltrados);
  if (!product || !items.length) return null;

  const idVersion = safeString(
    nrGetValue(product, ["idVersion", "IdVersion"]),
  );

  if (idVersion) {
    const matchByVersion = items.find(
      (item) =>
        safeString(nrGetValue(item, ["idVersion", "IdVersion"])) ===
        idVersion,
    );
    if (matchByVersion) return matchByVersion;
  }

  // Respaldo si idVersion no viniera en alguno de los dos catálogos:
  // submarca + año modelo también identifican el mismo vehículo real.
  const idSubmarca = safeString(
    nrGetValue(product, ["idSubmarca", "IdSubmarca"]),
  );
  const anioModelo = safeString(
    nrGetValue(product, ["anioModelo", "AnioModelo", "anioCatalogo", "AnioCatalogo"]),
  );

  if (!idSubmarca || !anioModelo) return null;

  return (
    items.find(
      (item) =>
        safeString(nrGetValue(item, ["idSubmarca", "IdSubmarca"])) ===
          idSubmarca &&
        safeString(
          nrGetValue(item, [
            "anioModelo",
            "AnioModelo",
            "anioCatalogo",
            "AnioCatalogo",
          ]),
        ) === anioModelo,
    ) || null
  );
}

function getProductExternalId(product = {}) {
  return safeString(
    nrGetValue(product, [
      "idExterno",
      "IdExterno",
      "anzenProductoId",
      "AnzenProductoId",
      "productoExternoId",
      "ProductoExternoId",
    ]),
  );
}

function getProductTypeId(product = {}) {
  return safeString(
    nrGetValue(product, [
      "idTipoProducto",
      "IdTipoProducto",
      "tipoProductoId",
      "TipoProductoId",
    ]),
  );
}

function getProductBrokerId(product = {}) {
  return safeString(nrGetValue(product, ["idBroker", "IdBroker"]));
}

function getPackageExternalId(item = {}) {
  return safeString(
    nrGetValue(item, [
      "idExterno",
      "IdExterno",
      "idPaqueteExterno",
      "IdPaqueteExterno",
    ]),
  );
}

function resolvePackageExternalId(overrideValue, packageItem, fallbackId) {
  const candidate =
    safeString(overrideValue) ||
    getPackageExternalId(packageItem) ||
    safeString(fallbackId);
  const numeric = Number(candidate);

  return Number.isFinite(numeric) && numeric > 0
    ? String(numeric)
    : safeString(fallbackId);
}

function getPackageCebipName(item = {}) {
  return safeString(
    nrGetValue(item, [
      "nombreCebip",
      "NombreCebip",
      "paqDescripcion",
      "PaqDescripcion",
      "nombre",
      "Nombre",
    ]),
  );
}

function getNrOpeningCommission(cotizacion) {
  return toNumber(
    pickDeepValue(cotizacion, [
      "montoCxa",
      "MontoCxa",
      "comisionApertura",
      "ComisionApertura",
    ]),
  );
}

function getNrOpeningCommissionPayment(cotizacion) {
  return toNumber(pickDeepValue(cotizacion, ["pagoCxa", "PagoCxa"]));
}

function getNrAnnualInsurance(cotizacion) {
  return toNumber(
    pickDeepValue(cotizacion, [
      "seguroAnual",
      "SeguroAnual",
      "montoSeguroAnual",
      "MontoSeguroAnual",
    ]),
  );
}

function getAnzenResponseObject(value) {
  if (!value) return {};
  if (Array.isArray(value)) return value[0] || {};
  if (typeof value === "object") return value;
  return {};
}

function normalizeAnzenInsuranceOptions(value) {
  const response = getAnzenResponseObject(value);
  const options = [];

  const pushOption = (item, kind, index) => {
    if (!item || typeof item !== "object") return;

    const insuranceId = safeString(
      nrGetValue(item, ["insuranceId", "InsuranceId", "id", "Id"]),
    );
    const campaignId = safeString(
      nrGetValue(item, ["campaignId", "CampaignId", "idCampaign", "IdCampaign"]),
    );
    const amount = toNumber(nrGetValue(item, ["amount", "Amount"]));
    const totalAmount = toNumber(
      nrGetValue(item, ["totalAmount", "TotalAmount", "montoTotal", "MontoTotal"]),
    );
    const surcharges = toNumber(
      nrGetValue(item, ["surcharges", "Surcharges", "recargos", "Recargos"]),
    );
    const promoAmount = toNumber(
      nrGetValue(item, ["promoAmount", "PromoAmount", "seguroPromocion", "SeguroPromocion"]),
    );
    const effectiveAmount =
      totalAmount ||
      amount ||
      promoAmount ||
      toNumber(nrGetValue(response, ["totalAmountSg", "TotalAmountSg"]));

    if (!insuranceId && !effectiveAmount) return;

    const kindLabel =
      kind === "free"
        ? "Seguro gratis"
        : kind === "promotional"
          ? "Seguro promocional"
          : "Seguro Anzen";

    options.push({
      ...item,
      __key: `${kind}-${insuranceId || "sin-id"}-${campaignId || "sin-campania"}-${index}`,
      __kind: kind,
      __label: `${kindLabel}${insuranceId ? ` ${insuranceId}` : ""} · ${formatCurrency(effectiveAmount)}`,
      __insuranceId: insuranceId,
      __campaignId: campaignId,
      __amount: amount || effectiveAmount,
      __totalAmount: totalAmount || effectiveAmount,
      __surcharges: surcharges,
    });
  };

  nrAsArray(response.insurances || response.Insurances).forEach((item, index) =>
    pushOption(item, "standard", index),
  );

  pushOption(response.insuranceFree || response.InsuranceFree, "free", 0);
  pushOption(
    response.insurancePromotional || response.InsurancePromotional,
    "promotional",
    0,
  );

  const totalAmountSg = toNumber(
    nrGetValue(response, ["totalAmountSg", "TotalAmountSg"]),
  );

  if (!options.length && totalAmountSg > 0) {
    options.push({
      __key: "summary-total-0",
      __kind: "summary",
      __label: `Seguro calculado por Anzen · ${formatCurrency(totalAmountSg)}`,
      __insuranceId: "",
      __campaignId: "",
      __amount: totalAmountSg,
      __totalAmount: totalAmountSg,
      __surcharges: 0,
    });
  }

  return options;
}

function buildInsurancePatch(option, responseValue) {
  if (!option) {
    return {
      seguroAnzenKey: "",
      seguroAnzenId: "",
      seguroAnzenCampaniaId: "",
      seguroAnzenAmount: "0",
      seguroAnzenTotalAmount: "0",
      seguroAnzenSurcharges: "0",
      seguroAnzenTipo: "",
    };
  }

  const response = getAnzenResponseObject(responseValue);
  const amount =
    toNumber(option.__totalAmount) ||
    toNumber(option.__amount) ||
    toNumber(nrGetValue(response, ["totalAmountSg", "TotalAmountSg"]));

  return {
    seguroAnzenKey: safeString(option.__key),
    seguroAnzenId: safeString(option.__insuranceId),
    seguroAnzenCampaniaId: safeString(option.__campaignId),
    seguroAnzenAmount: safeString(option.__amount || amount),
    seguroAnzenTotalAmount: safeString(option.__totalAmount || amount),
    seguroAnzenSurcharges: safeString(option.__surcharges || 0),
    seguroAnzenTipo: safeString(option.__kind),
    montoSeguro: safeString(amount),
  };
}

function normalizeKey(key = "") {
  return String(key).toLowerCase().replace(/[\s_-]/g, "");
}

function nrGetValue(item = {}, keys = []) {
  if (!item || typeof item !== "object") return "";

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(item, key)) return item[key];
  }

  const normalized = Object.entries(item).reduce((acc, [key, value]) => {
    acc[normalizeKey(key)] = value;
    return acc;
  }, {});

  for (const key of keys) {
    const normalizedKey = normalizeKey(key);
    if (Object.prototype.hasOwnProperty.call(normalized, normalizedKey)) {
      return normalized[normalizedKey];
    }
  }

  return "";
}

function nrGetId(item = {}, extraKeys = []) {
  return nrGetValue(item, [
    ...extraKeys,
    "id",
    "Id",
    "ID",
    "idMarca",
    "IdMarca",
    "marcaId",
    "MarcaId",
    "idSubmarca",
    "IdSubmarca",
    "submarcaId",
    "SubmarcaId",
    "idVersion",
    "IdVersion",
    "versionId",
    "VersionId",
    "idProducto",
    "IdProducto",
    "productoId",
    "ProductoId",
    "idPaquete",
    "IdPaquete",
    "paqueteId",
    "PaqueteId",
    "idPlan",
    "IdPlan",
    "planId",
    "PlanId",
    "idPlazo",
    "IdPlazo",
    "plazoId",
    "PlazoId",
    "stateId",
    "StateId",
    "municipalityId",
    "MunicipalityId",
    "insuranceId",
    "InsuranceId",
    "typeOfCoverageId",
    "TypeOfCoverageId",
    "typeOfUseId",
    "TypeOfUseId",
    "typeOfUsesId",
    "TypeOfUsesId",
    "udiLevelId",
    "UdiLevelId",
  ]);
}

function nrGetName(item = {}, extraKeys = []) {
  return nrGetValue(item, [
    ...extraKeys,
    "nombre",
    "Nombre",
    "name",
    "Name",
    "descripcion",
    "Descripcion",
    "description",
    "Description",
    "submarca",
    "Submarca",
    "descripcionSubmarca",
    "DescripcionSubmarca",
    "version",
    "Version",
    "descripcionVersion",
    "DescripcionVersion",
    "plan",
    "Plan",
    "paquete",
    "Paquete",
    "estado",
    "Estado",
    "municipio",
    "Municipio",
    "insuranceName",
    "InsuranceName",
    "coverage",
    "Coverage",
    "typeOfCoverage",
    "TypeOfCoverage",
    "use",
    "Use",
    "typeOfUse",
    "TypeOfUse",
    "tipoSeguroVida",
    "TipoSeguroVida",
    "subsidio",
    "Subsidio",
    "nombreCebip",
    "NombreCebip",
  ]);
}

function findById(options = [], id, extraKeys = []) {
  return nrAsArray(options).find((item) => String(nrGetId(item, extraKeys)) === String(id));
}

function pickDeepValue(value, wantedKeys = []) {
  const queue = [value];
  const normalizedWanted = wantedKeys.map(normalizeKey);
  const seen = new Set();

  while (queue.length) {
    const current = queue.shift();
    if (!current || typeof current !== "object") continue;
    if (seen.has(current)) continue;
    seen.add(current);

    for (const [key, inner] of Object.entries(current)) {
      const normalized = normalizeKey(key);
      if (normalizedWanted.includes(normalized)) return inner;
      if (inner && typeof inner === "object") queue.push(inner);
    }
  }

  return "";
}

/*
 * A diferencia de pickDeepValue, esta función sí respeta el orden de
 * prioridad de wantedKeys. Es importante para respuestas que contienen
 * tanto pagoInicial como montoEnganche.
 */
function pickDeepValueByPriority(value, wantedKeys = []) {
  for (const key of wantedKeys) {
    const found = pickDeepValue(value, [key]);

    if (found !== "" && found !== null && found !== undefined) {
      return found;
    }
  }

  return "";
}

function getQuoteCalculationSignature(quote = {}) {
  const fields = [
    "agenciaClave",
    "agenciaId",
    "clasificacionId",
    "personalidadJuridica",
    "idPerJuridica",
    "tipoOperacionId",
    "estadoId",
    "municipioId",
    "marcaId",
    "modeloId",
    "anio",
    "versionId",
    "estrategiaId",
    "productoId",
    "productoTipoPrecioId",
    "anzenProductoId",
    "idTipoProducto",
    "idBroker",
    "planId",
    "paqueteExternoId",
    "plazo",
    "precioVehiculo",
    "enganche",
    "porcentajeEnganche",
    "tasaEditadaNR",
    "accesoriosFinanciados",
    "accesoriosContado",
    "garantiaExtendida",
    "productosAdicionales",
    "tipoSeguroId",
    "seguroVidaId",
    "aseguradoraId",
    "coberturaId",
    "tipoUsoId",
    "udiId",
    "tipoCompraSeguro",
    "montoSeguro",
    "gpsId",
    "seguroAnzenKey",
    "seguroAnzenId",
    "seguroAnzenCampaniaId",
    "seguroAnzenAmount",
    "seguroAnzenTotalAmount",
    "seguroAnzenSurcharges",
    "seguroAnzenTipo",
  ];

  return JSON.stringify(
    fields.reduce((result, field) => {
      result[field] = safeString(quote[field]);
      return result;
    }, {}),
  );
}

function getNrMonthlyPayment(cotizacion) {
  return toNumber(
    pickDeepValue(cotizacion, [
      "pagoMensualTotal",
      "PagoMensualTotal",
      "mensualidad",
      "Mensualidad",
      "mensualidadCalculada",
      "MensualidadCalculada",
      "pagoMensual",
      "PagoMensual",
      "renta",
      "Renta",
      "montoMensual",
      "MontoMensual",
    ]),
  );
}

function getNrDownPayment(cotizacion) {
  return toNumber(
    pickDeepValueByPriority(cotizacion, [
      "pagoInicial",
      "PagoInicial",
      "montoInicial",
      "MontoInicial",
      "montoEnganche",
      "MontoEnganche",
      "enganche",
      "Enganche",
    ]),
  );
}

function getNrReportedDownPayment(cotizacion) {
  return toNumber(
    pickDeepValueByPriority(cotizacion, [
      "montoEnganche",
      "MontoEnganche",
      "enganche",
      "Enganche",
    ]),
  );
}

// CORRECCIÓN sobre una conclusión anterior: con dos cotizaciones reales
// donde garantía extendida y accesorios de contado tenían montos DISTINTOS
// (antes coincidían por casualidad, por eso parecía ser gEX), quedó claro
// que el delta real es el monto de "Accesorios de contado" (quote.accesoriosContado),
// NO montoGarantiaExtendida. Ese campo no viene en la respuesta de /cotiza
// (es un dato de entrada), así que hay que pasarlo aparte.
// Sigue siendo evidencia de solo dos casos — no se encontró una fórmula
// igual de limpia para pagoMensualTotal ni pagoInicial (las diferencias ahí
// no coinciden con ningún campo o combinación obvia), así que esos dos NO
// se ajustan, para no volver a adivinar mal. Vale la pena confirmar con NR
// qué representa exactamente "Monto a Financiar" en el PDF.
function getNrFinancedAmount(cotizacion, accesoriosContado = 0) {
  const base = toNumber(
    pickDeepValue(cotizacion, [
      "montoFinanciarTotal",
      "MontoFinanciarTotal",
      "montoFinanciar",
      "MontoFinanciar",
      "montoAFinanciar",
      "MontoAFinanciar",
      "capitalFinanciado",
      "CapitalFinanciado",
    ]),
  );

  return base + toNumber(accesoriosContado);
}

function getNrRate(cotizacion) {
  if (!cotizacion || typeof cotizacion !== "object") return 0;

  /*
   * La tasa principal se obtiene exclusivamente de la respuesta de:
   * POST /api/v1/quotations/cotiza
   *
   * No se usa `tasaEditada`, ya que ese campo puede regresar valores de
   * control y no necesariamente representa la tasa financiera de la unidad.
   *
   * Tampoco se usan tasaInteresSeguro, tasaProductoAdic ni tasaAccesorio,
   * porque corresponden a conceptos diferentes al financiamiento del auto.
   */
  const tasaUnidad = toNumber(
    pickDeepValue(cotizacion, ["tasaUnidad", "TasaUnidad"]),
  );

  const tasa = toNumber(
    pickDeepValue(cotizacion, ["tasa", "Tasa"]),
  );

  const tasaPonderada = toNumber(
    pickDeepValue(cotizacion, ["tasaPonderada", "TasaPonderada"]),
  );

  const tasaUnidadOriginal = toNumber(
    pickDeepValue(cotizacion, [
      "tasaUnidadOriginal",
      "TasaUnidadOriginal",
    ]),
  );

  return (
    tasaUnidad ||
    tasa ||
    tasaPonderada ||
    tasaUnidadOriginal ||
    0
  );
}

function getNrUnitPayment(cotizacion) {
  return toNumber(pickDeepValue(cotizacion, ["pagoUnidad", "PagoUnidad"]));
}

function getNrInsurancePayment(cotizacion) {
  return toNumber(
    pickDeepValue(cotizacion, [
      "pagoSeguro",
      "PagoSeguro",
      "montoSeguro",
      "MontoSeguro",
    ]),
  );
}

function getNrLifeInsurancePayment(cotizacion) {
  const lifeInsuranceWithMargin = toNumber(
    pickDeepValue(cotizacion, [
      "seguroVidaConMargen",
      "SeguroVidaConMargen",
    ]),
  );

  if (lifeInsuranceWithMargin > 0) {
    return lifeInsuranceWithMargin;
  }

  const lifeInsurance = toNumber(
    pickDeepValue(cotizacion, [
      "seguroDeVida",
      "SeguroDeVida",
    ]),
  );

  if (lifeInsurance > 0) {
    return lifeInsurance;
  }

  const monthlyInsuranceService = toNumber(
    pickDeepValue(cotizacion, [
      "mensualidadServicioSeguro",
      "MensualidadServicioSeguro",
    ]),
  );

  if (monthlyInsuranceService > 0) {
    return monthlyInsuranceService;
  }

  return toNumber(
    pickDeepValue(cotizacion, [
      "pagoAdicional",
      "PagoAdicional",
    ]),
  );
}


function normalizePdfBase64(pdfBase64 = "") {
  let clean = safeString(pdfBase64);

  if (!clean) {
    return "";
  }

  const commaIndex = clean.indexOf(",");

  if (clean.startsWith("data:") && commaIndex >= 0) {
    clean = clean.slice(commaIndex + 1);
  }

  clean = clean
    .replace(/\s+/g, "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const missingPadding = clean.length % 4;

  if (missingPadding) {
    clean += "=".repeat(4 - missingPadding);
  }

  return clean;
}

function createPdfBlob(pdfBase64 = "") {
  const clean = normalizePdfBase64(pdfBase64);

  if (!clean || typeof window === "undefined") {
    return null;
  }

  try {
    const binary = window.atob(clean);
    const chunkSize = 1024;
    const chunks = [];

    for (let offset = 0; offset < binary.length; offset += chunkSize) {
      const slice = binary.slice(offset, offset + chunkSize);
      const bytes = new Uint8Array(slice.length);

      for (let index = 0; index < slice.length; index += 1) {
        bytes[index] = slice.charCodeAt(index);
      }

      chunks.push(bytes);
    }

    return new Blob(chunks, { type: "application/pdf" });
  } catch (error) {
    console.error("No se pudo convertir el PDF Base64 a Blob:", error);
    return null;
  }
}

function getOptionLabel(option) {
  if (!option) return "";
  if (typeof option !== "object") return safeString(option);

  const name = nrGetName(option);
  const id = nrGetId(option);
  const anio = nrGetValue(option, ["anio", "Anio", "anioModelo", "AnioModelo"]);
  const plazo = nrGetValue(option, ["valor", "Valor", "plazo", "Plazo", "meses", "Meses", "term", "Term"]);

  return [name || `ID ${id || "N/A"}`, anio, plazo ? `${plazo} meses` : ""]
    .filter(Boolean)
    .join(" · ");
}

function getPlazoValue(option) {
  return (
    nrGetValue(option, ["valor", "Valor", "plazo", "Plazo", "meses", "Meses", "term", "Term"]) ||
    nrGetId(option)
  );
}

function Icon({ name, size = 18, className = "" }) {
  const commonProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
    "aria-hidden": true,
  };

  const icons = {
    user: (
      <>
        <path d="M20 21a8 8 0 0 0-16 0" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
    building: (
      <>
        <path d="M3 21h18" />
        <path d="M6 21V5l6-3 6 3v16" />
        <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />
      </>
    ),
    car: (
      <>
        <path d="M5 17h14" />
        <path d="M6 17v2M18 17v2" />
        <path d="m4 13 2-5a2 2 0 0 1 1.9-1.3h8.2A2 2 0 0 1 18 8l2 5" />
        <path d="M3 13h18v4H3z" />
        <path d="M7 14.8h.01M17 14.8h.01" />
      </>
    ),
    shield: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    wallet: (
      <>
        <path d="M20 7V5a2 2 0 0 0-2-2H5a3 3 0 0 0 0 6h15v10a2 2 0 0 1-2 2H5a3 3 0 0 1-3-3V6" />
        <path d="M16 13h2" />
      </>
    ),
    chart: (
      <>
        <path d="M3 3v18h18" />
        <path d="m7 16 4-4 3 3 5-7" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 4v5h5" />
        <path d="M4 13a8.1 8.1 0 0 0 15.5 2M20 20v-5h-5" />
      </>
    ),
    trash: (
      <>
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2M19 6l-1 14H6L5 6" />
        <path d="M10 11v5M14 11v5" />
      </>
    ),
    sparkles: (
      <>
        <path d="m12 3-1.2 3.1L8 7.3l2.8 1.2L12 12l1.2-3.5L16 7.3l-2.8-1.2L12 3Z" />
        <path d="m19 13-.8 2.1-2.2.9 2.2.9L19 19l.8-2.1 2.2-.9-2.2-.9L19 13Z" />
        <path d="m5 14-.7 1.8-1.8.7 1.8.7L5 19l.7-1.8 1.8-.7-1.8-.7L5 14Z" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    alert: (
      <>
        <path d="M10.3 3.7 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4M12 17h.01" />
      </>
    ),
    file: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
        <path d="M14 2v6h6M8 13h8M8 17h6" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v12" />
        <path d="m7 10 5 5 5-5" />
        <path d="M5 21h14" />
      </>
    ),
    save: (
      <>
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
        <path d="M17 21v-8H7v8M7 3v5h8" />
      </>
    ),
    calculator: (
      <>
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <path d="M8 6h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h.01M12 19h.01M16 19h.01" />
      </>
    ),
    chevron: <path d="m9 18 6-6-6-6" />,
    info: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8h.01" />
      </>
    ),
    loader: (
      <>
        <path d="M21 12a9 9 0 1 1-6.2-8.6" />
      </>
    ),
  };

  return <svg {...commonProps}>{icons[name] || icons.sparkles}</svg>;
}

function FieldHelp({ children }) {
  if (!children) return null;
  return (
    <small className="nr-field-help">
      <Icon name="info" size={14} />
      <span>{children}</span>
    </small>
  );
}

function FieldLabel({ label, required = false, optional = false }) {
  return (
    <label className="form-label nr-field-label">
      <span>{label}</span>
      {required && <span className="nr-required">Requerido</span>}
      {optional && <span className="nr-optional">Opcional</span>}
    </label>
  );
}

function CatalogSelect({
  label,
  value,
  options,
  onChange,
  disabled = false,
  loading = false,
  placeholder = "Seleccione una opción",
  getValue = nrGetId,
  getLabel = getOptionLabel,
  help = "",
  required = false,
  optional = false,
}) {
  const safeOptions = nrAsArray(options);
  const isDisabled = disabled || loading;
  const currentPlaceholder = loading
    ? "Cargando opciones..."
    : safeOptions.length
      ? placeholder
      : "Sin opciones disponibles";

  return (
    <div className="nr-field">
      <FieldLabel label={label} required={required} optional={optional} />
      <div className="nr-control-wrap">
        <select
          className={`form-select nr-control ${value ? "nr-control-filled" : ""}`}
          value={value || ""}
          disabled={isDisabled}
          aria-label={label}
          onChange={(event) => onChange(event.target.value, findByCustomValue(safeOptions, event.target.value, getValue))}
        >
          <option value="">{currentPlaceholder}</option>
          {safeOptions.map((option, index) => {
            const optionValue = safeString(getValue(option)) || String(index);
            return (
              <option key={`${optionValue}-${index}`} value={optionValue}>
                {getLabel(option) || optionValue}
              </option>
            );
          })}
        </select>
        {loading && <Icon name="loader" size={17} className="nr-spin nr-control-loader" />}
      </div>
      <FieldHelp>{help}</FieldHelp>
    </div>
  );
}

function findByCustomValue(options = [], value, getValue) {
  return nrAsArray(options).find((item, index) => {
    const itemValue = safeString(getValue(item)) || String(index);
    return String(itemValue) === String(value);
  });
}

function MoneyInput({
  label,
  value,
  onChange,
  help = "",
  readOnly = false,
  required = false,
  optional = false,
  placeholder = "0.00",
}) {
  return (
    <div className="nr-field">
      <FieldLabel label={label} required={required} optional={optional} />
      <div className={`nr-money-control ${readOnly ? "nr-readonly" : ""}`}>
        <span className="nr-money-prefix">$</span>
        <input
          type="text"
          inputMode="decimal"
          className="form-control nr-control"
          value={value ?? ""}
          readOnly={readOnly}
          placeholder={placeholder}
          aria-label={label}
          onChange={(event) => onChange(event.target.value.replace(/[^0-9.]/g, ""))}
        />
        <span className="nr-money-suffix">MXN</span>
      </div>
      <FieldHelp>{help}</FieldHelp>
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  help = "",
  placeholder = "",
  readOnly = false,
  required = false,
  optional = false,
}) {
  return (
    <div className="nr-field">
      <FieldLabel label={label} required={required} optional={optional} />
      <input
        type={type}
        className={`form-control nr-control ${value ? "nr-control-filled" : ""}`}
        value={value ?? ""}
        readOnly={readOnly}
        placeholder={placeholder}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldHelp>{help}</FieldHelp>
    </div>
  );
}

function ReadOnlyMetric({ label, value, muted = false, highlight = false, icon = "chart" }) {
  return (
    <div className={`nr-metric h-100 ${muted ? "nr-metric-muted" : ""} ${highlight ? "nr-metric-highlight" : ""}`}>
      <div className="nr-metric-icon"><Icon name={icon} size={18} /></div>
      <div>
        <div className="nr-metric-label">{label}</div>
        <div className="nr-metric-value">{value}</div>
      </div>
    </div>
  );
}

function SectionCard({ id, step, title, subtitle, icon, done = false, loading = false, children, action = null }) {
  return (
    <section id={id} className={`nr-card nr-section-card ${done ? "nr-section-complete" : ""}`}>
      <header className="nr-section-header">
        <div className="nr-section-heading">
          <div className="nr-section-icon"><Icon name={icon} size={21} /></div>
          <div>
            <div className="nr-section-kicker">Paso {step}</div>
            <h5 className="nr-section-title">{title}</h5>
            {subtitle && <p className="nr-section-subtitle">{subtitle}</p>}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
          {loading && (
            <span className="nr-status nr-status-loading">
              <Icon name="loader" size={14} className="nr-spin" /> Cargando
            </span>
          )}
          {!loading && (
            <span className={`nr-status ${done ? "nr-status-success" : "nr-status-pending"}`}>
              <Icon name={done ? "check" : "alert"} size={14} />
              {done ? "Completo" : "Pendiente"}
            </span>
          )}
          {action}
        </div>
      </header>
      <div className="nr-section-body">{children}</div>
    </section>
  );
}

function SelectField({ label, value, onChange, children, help = "", required = false, optional = false, disabled = false }) {
  return (
    <div className="nr-field">
      <FieldLabel label={label} required={required} optional={optional} />
      <select
        className={`form-select nr-control ${value ? "nr-control-filled" : ""}`}
        value={value}
        disabled={disabled}
        aria-label={label}
        onChange={onChange}
      >
        {children}
      </select>
      <FieldHelp>{help}</FieldHelp>
    </div>
  );
}

export default function CreateVentaNRUnified() {
  const [client, setClient] = useState(() => ({
    ...EMPTY_CLIENT,
    asesor: localStorage.getItem("userName") || localStorage.getItem("nombre") || "",
    user_id: localStorage.getItem("userId") || "",
  }));
  const [quote, setQuote] = useState(EMPTY_QUOTE);
  const [catalogs, setCatalogs] = useState(EMPTY_CATALOGS);
  const [loading, setLoading] = useState({});
  const [lastError, setLastError] = useState("");
  const [nrCotizacion, setNrCotizacion] = useState(null);
  const [anzenCotizacion, setAnzenCotizacion] = useState(null);
  const [anzenError, setAnzenError] = useState(null);
  const [idCotizacion, setIdCotizacion] = useState("");
  const [lastCotizaRequest, setLastCotizaRequest] = useState(null);
  const [lastCotizaSignature, setLastCotizaSignature] = useState("");
  const [lastSavedSignature, setLastSavedSignature] = useState("");
  const [pdfBase64, setPdfBase64] = useState("");
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("asesor");
  const [rawResponseOpen, setRawResponseOpen] = useState(false);

  const currentQuoteSignature = useMemo(
    () => getQuoteCalculationSignature(quote),
    [quote],
  );
  const isCurrentQuoteSaved = Boolean(
    idCotizacion &&
      lastSavedSignature &&
      lastSavedSignature === currentQuoteSignature,
  );

  const pdfBlob = useMemo(() => createPdfBlob(pdfBase64), [pdfBase64]);
  const pdfObjectUrl = useMemo(
    () => (pdfBlob ? URL.createObjectURL(pdfBlob) : ""),
    [pdfBlob],
  );

  useEffect(
    () => () => {
      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
    },
    [pdfObjectUrl],
  );

  const nrMensualidad = useMemo(() => getNrMonthlyPayment(nrCotizacion), [nrCotizacion]);
  const nrPagoInicial = useMemo(() => getNrDownPayment(nrCotizacion), [nrCotizacion]);
  const nrEngancheReportado = useMemo(
    () => getNrReportedDownPayment(nrCotizacion),
    [nrCotizacion],
  );
  const nrMontoFinanciar = useMemo(
    () => getNrFinancedAmount(nrCotizacion, quote.accesoriosContado),
    [nrCotizacion, quote.accesoriosContado],
  );
  const nrTasa = useMemo(() => getNrRate(nrCotizacion), [nrCotizacion]);
  const nrPagoUnidad = useMemo(() => getNrUnitPayment(nrCotizacion), [nrCotizacion]);
  const nrPagoSeguro = useMemo(() => getNrInsurancePayment(nrCotizacion), [nrCotizacion]);
  const nrPagoSeguroVida = useMemo(
    () => getNrLifeInsurancePayment(nrCotizacion),
    [nrCotizacion],
  );
  const nrMontoCxa = useMemo(() => getNrOpeningCommission(nrCotizacion), [nrCotizacion]);
  const nrPagoCxa = useMemo(
    () => getNrOpeningCommissionPayment(nrCotizacion),
    [nrCotizacion],
  );
  const nrSeguroAnual = useMemo(() => getNrAnnualInsurance(nrCotizacion), [nrCotizacion]);
  const anzenInsuranceOptions = useMemo(
    () => normalizeAnzenInsuranceOptions(anzenCotizacion),
    [anzenCotizacion],
  );
  const selectedAnzenInsurance = useMemo(
    () =>
      anzenInsuranceOptions.find(
        (item) => safeString(item.__key) === safeString(quote.seguroAnzenKey),
      ) || null,
    [anzenInsuranceOptions, quote.seguroAnzenKey],
  );

  const requestedDownPayment = useMemo(() => {
    const directDownPayment = toNumber(quote.enganche);

    if (directDownPayment > 0) {
      return directDownPayment;
    }

    return Math.round(
      (toNumber(quote.precioVehiculo) *
        toNumber(quote.porcentajeEnganche)) /
        100,
    );
  }, [
    quote.enganche,
    quote.porcentajeEnganche,
    quote.precioVehiculo,
  ]);

  const hasDownPaymentMismatch = Boolean(
    nrCotizacion &&
      nrEngancheReportado > 0 &&
      Math.abs(nrEngancheReportado - requestedDownPayment) > 0.01,
  );

  const quoteDiagnostics = useMemo(
    () => ({
      engancheEnviado: requestedDownPayment,
      porcentajeEngancheEnviado: toNumber(quote.porcentajeEnganche),
      engancheRegresadoPorNr: nrEngancheReportado,
      pagoInicialRegresadoPorNr: nrPagoInicial,
      montoFinanciarRegresadoPorNr: nrMontoFinanciar,
      diferenciaEnganche:
        nrEngancheReportado && requestedDownPayment
          ? Number(
              (nrEngancheReportado - requestedDownPayment).toFixed(2),
            )
          : 0,
      existeDiferenciaEnganche: hasDownPaymentMismatch,
      cotizacionActualGuardada: isCurrentQuoteSaved,
    }),
    [
      hasDownPaymentMismatch,
      isCurrentQuoteSaved,
      nrEngancheReportado,
      nrMontoFinanciar,
      nrPagoInicial,
      quote.porcentajeEnganche,
      requestedDownPayment,
    ],
  );

  useEffect(() => {
    if (
      lastCotizaSignature &&
      lastCotizaSignature !== currentQuoteSignature
    ) {
      setNrCotizacion(null);
      setPdfBase64("");
      setPdfPreviewOpen(false);
    }
  }, [currentQuoteSignature, lastCotizaSignature]);

  const patchClient = useCallback((patch) => {
    setClient((prev) => ({ ...prev, ...patch }));
  }, []);

  const patchQuote = useCallback((patch) => {
    setQuote((prev) => ({
      ...prev,
      ...(typeof patch === "function" ? patch(prev) : patch),
    }));
  }, []);

  const setBusy = useCallback((key, value) => {
    setLoading((prev) => ({ ...prev, [key]: Boolean(value) }));
  }, []);

  const apiPost = useCallback(async (endpoint, payload = {}) => {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });

    let json = {};
    try {
      json = await response.json();
    } catch (error) {
      throw new Error("La API respondió sin JSON válido.");
    }

    const responseData = json?.data ?? json;
    const statusCode = Number(json?.statusCode || response.status || 0);
    const problemStatus = Number(
      responseData?.status ||
        responseData?.Status ||
        0,
    );
    const problemType = safeString(
      responseData?.type ||
        responseData?.Type,
    );
    const problemCode = safeString(
      responseData?.code ||
        responseData?.Code,
    );

    const errorLike =
      !response.ok ||
      statusCode >= 400 ||
      problemStatus >= 400 ||
      problemType.toLowerCase().includes("/errors/") ||
      String(responseData?.type || responseData?.Type || "").toUpperCase() ===
        "ERROR";

    if (errorLike) {
      const apiError = new Error(
        json?.message ||
          responseData?.detail ||
          responseData?.title ||
          responseData?.message ||
          `Error consultando ${endpoint}`,
      );

      apiError.code = problemCode;
      apiError.status = problemStatus || statusCode;
      apiError.payload = responseData;

      throw apiError;
    }

    return responseData;
  }, []);

  const apiPostSafe = useCallback(
    async (endpoint, payload = {}, fallback = []) => {
      try {
        return await apiPost(endpoint, payload);
      } catch (error) {
        console.warn(`No se pudo consultar ${endpoint}:`, error);
        setLastError(error.message || String(error));
        return fallback;
      }
    },
    [apiPost],
  );

  const loadBaseCatalogs = useCallback(async () => {
    setBusy("base", true);
    setLastError("");

    try {
      const [clasificacionesRaw, estadosRaw] = await Promise.all([
        apiPostSafe(NR_ENDPOINTS.clasificaciones, {}, []),
        apiPostSafe(NR_ENDPOINTS.estados, {}, []),
      ]);

      const clasificaciones = nrAsArray(clasificacionesRaw);
      const estados = nrAsArray(estadosRaw);

      setCatalogs((prev) => ({
        ...prev,
        clasificaciones,
        estados,
      }));

      if (!clasificaciones.length) {
        setLastError(
          "NR no regresó el catálogo de clasificaciones (Nuevo/Seminuevo).",
        );
      }
    } finally {
      setBusy("base", false);
    }
  }, [apiPostSafe, setBusy]);

  const loadAnios = useCallback(async () => {
    setBusy("anios", true);

    try {
      // El servicio de años se consulta sin filtros.
      const aniosRaw = await apiPost(NR_ENDPOINTS.anios, {});
      const anios = normalizeNrYears(aniosRaw);

      setCatalogs((prev) => ({
        ...prev,
        anios,
      }));

      if (!anios.length) {
        setLastError(
          "El servicio de años respondió correctamente, pero no regresó opciones.",
        );
      }
    } catch (error) {
      console.warn("No se pudo consultar el catálogo de años NR:", error);

      setCatalogs((prev) => ({
        ...prev,
        anios: [],
      }));

      setLastError(
        error.message ||
          "No fue posible consultar el catálogo de años de NR.",
      );
    } finally {
      setBusy("anios", false);
    }
  }, [apiPost, setBusy]);

  const handleReloadCatalogs = useCallback(async () => {
    await Promise.all([
      loadBaseCatalogs(),
      loadAnios(),
    ]);
  }, [loadAnios, loadBaseCatalogs]);

  const loadMarcas = useCallback(async () => {
    if (!quote.clasificacionId) return;
    setBusy("marcas", true);
    try {
      const marcasRaw = await apiPostSafe(NR_ENDPOINTS.marcas, {
        clasifId: Number(quote.clasificacionId),
        agenciaId: Number(quote.agenciaId || NR_DEFAULT_AGENCIA_ID),
      });
      setCatalogs((prev) => ({ ...prev, marcas: nrAsArray(marcasRaw) }));
    } finally {
      setBusy("marcas", false);
    }
  }, [apiPostSafe, quote.agenciaId, quote.clasificacionId, setBusy]);

  const loadModelos = useCallback(async () => {
    if (!quote.marcaId) return;
    setBusy("modelos", true);
    try {
      const modelosRaw = await apiPostSafe(NR_ENDPOINTS.modelos, {
        marcaId: Number(quote.marcaId),
        clasifId: Number(quote.clasificacionId),
        agenciaId: Number(quote.agenciaId || NR_DEFAULT_AGENCIA_ID),
      });
      setCatalogs((prev) => ({ ...prev, modelos: nrAsArray(modelosRaw) }));
    } finally {
      setBusy("modelos", false);
    }
  }, [apiPostSafe, quote.agenciaId, quote.clasificacionId, quote.marcaId, setBusy]);

  const loadVersiones = useCallback(async () => {
    if (!quote.marcaId || !quote.modeloId || !quote.anio) return;
    setBusy("versiones", true);
    try {
      const versionesRaw = await apiPostSafe(NR_ENDPOINTS.versiones, {
        clasifId: Number(quote.clasificacionId),
        marcaId: Number(quote.marcaId),
        submarcaId: Number(quote.modeloId),
        anio: Number(quote.anio),
      });
      setCatalogs((prev) => ({ ...prev, versiones: nrAsArray(versionesRaw) }));
    } finally {
      setBusy("versiones", false);
    }
  }, [apiPostSafe, quote.anio, quote.clasificacionId, quote.marcaId, quote.modeloId, setBusy]);

  const loadEstrategiasYProductos = useCallback(async () => {
    if (!quote.marcaId || !quote.modeloId || !quote.versionId || !quote.anio) return;
    setBusy("productos", true);

    try {
      const estrategiasRaw = await apiPostSafe(NR_ENDPOINTS.estrategias, {
        clasifId: Number(quote.clasificacionId),
        submarcaId: Number(quote.modeloId),
        anio: Number(quote.anio),
        versionId: Number(quote.versionId),
      });

      const estrategias = nrAsArray(estrategiasRaw);
      const defaultStrategy = getDefaultCatalogItem(estrategias) || estrategias[0] || null;
      const estrategiaId = quote.estrategiaId || String(nrGetId(defaultStrategy || {}) || "");

      if (!quote.estrategiaId && estrategiaId) {
        const estrategia = findById(estrategias, estrategiaId);
        patchQuote({
          estrategiaId,
          estrategiaNombre: nrGetName(estrategia) || "",
        });
      }

      const [productosRaw, productosFiltradosRaw] = await Promise.all([
        estrategiaId
          ? apiPostSafe(NR_ENDPOINTS.productos, {
              clasifId: Number(quote.clasificacionId),
              marcaId: Number(quote.marcaId),
              submarcaId: Number(quote.modeloId),
              versionId: Number(quote.versionId),
              anioId: Number(quote.anio),
              estrategiaId: Number(estrategiaId),
              agenciaId: Number(quote.agenciaId || NR_DEFAULT_AGENCIA_ID),
            })
          : [],
        apiPostSafe(NR_ENDPOINTS.productoFiltrado, {
          idMarca: Number(quote.marcaId),
          destino: 1,
        }),
      ]);

      const productosFiltrados = nrAsArray(productosFiltradosRaw);
      const productosFiltradosCoincidentes = productosFiltrados.filter((item) => {
        const versionId = safeString(
          nrGetValue(item, ["idVersion", "IdVersion", "versionId", "VersionId"]),
        );
        const modeloId = safeString(
          nrGetValue(item, ["idSubmarca", "IdSubmarca", "submarcaId", "SubmarcaId"]),
        );
        const anio = safeString(
          nrGetValue(item, ["anioModelo", "AnioModelo", "anioCatalogo", "AnioCatalogo"]),
        );

        return (
          (!versionId || versionId === safeString(quote.versionId)) &&
          (!modeloId || modeloId === safeString(quote.modeloId)) &&
          (!anio || anio === safeString(quote.anio))
        );
      });

      const productosFromServicio = nrCatalogItems(productosRaw);
      const productosFromNr = [
        ...productosFromServicio,
        ...productosFiltradosCoincidentes,
      ].filter((item, index, items) => {
        const productId = safeString(
          nrGetValue(item, [
            "idProducto",
            "IdProducto",
            "productoId",
            "ProductoId",
            "id",
            "Id",
          ]),
        );

        if (!productId || productId === "0") return false;

        // /catalogs/productos y /catalogs/producto-filtrado pueden traer
        // idProducto DISTINTO para el mismo vehículo real. Sin esto, el
        // mismo vehículo aparecía dos veces en el selector — una válida
        // para guardar (la de /catalogs/productos, que va primero en este
        // arreglo) y otra que solo sirve para sacar el idExterno de Anzen
        // pero rompe el guardado si se usa como IdProducto. Se conserva
        // únicamente la primera aparición de cada idVersion.
        const idVersion = safeString(
          nrGetValue(item, ["idVersion", "IdVersion"]),
        );

        return (
          items.findIndex((candidate) => {
            const candidateId = safeString(
              nrGetValue(candidate, [
                "idProducto",
                "IdProducto",
                "productoId",
                "ProductoId",
                "id",
                "Id",
              ]),
            );

            if (candidateId === productId) return true;

            if (idVersion) {
              const candidateVersion = safeString(
                nrGetValue(candidate, ["idVersion", "IdVersion"]),
              );
              if (candidateVersion && candidateVersion === idVersion) {
                return true;
              }
            }

            return false;
          }) === index
        );
      });

      const productosValidos = productosFromNr.filter((item) => {
        const productId = safeString(
          nrGetValue(item, [
            "idProducto",
            "IdProducto",
            "productoId",
            "ProductoId",
            "id",
            "Id",
          ]),
        );
        return Boolean(productId && productId !== "0");
      });
      const productos = dedupeNrProducts(productosValidos);

      setCatalogs((prev) => ({
        ...prev,
        estrategias,
        productos,
        productosFiltrados,
      }));

      // Se busca el producto seleccionado únicamente entre los productos reales
      // regresados por NR. Si NR no trae ninguno, defaultProduct queda null y
      // no se rellena nada: la UI debe mostrar "Sin opciones disponibles".
      const currentNrProduct = quote.productoId
        ? findById(productosValidos, quote.productoId, ["idProducto", "IdProducto"])
        : null;

      const defaultProduct =
        currentNrProduct ||
        getDefaultCatalogItem(productosValidos) ||
        productosValidos[0] ||
        null;

      const defaultProductId = defaultProduct
        ? safeString(nrGetId(defaultProduct, ["idProducto", "IdProducto"]))
        : "";
      const enrichedProduct =
        (defaultProduct &&
          findProductInFiltradoByVersion(productosFiltrados, defaultProduct)) ||
        defaultProduct;

      if (defaultProduct && (!quote.productoId || !currentNrProduct)) {
        patchQuote({
          productoId: defaultProductId,
          productoNombre:
            nrGetName(defaultProduct, [
              "descripcion",
              "Descripcion",
              "descripcionVersion",
              "DescripcionVersion",
            ]) || "No disponible",
          // Si NR no trae el tipo de precio del producto, se queda vacío:
          // guardar-cotizacion debe rechazar la operación, no inventar "1".
          productoTipoPrecioId: safeString(
            nrGetValue(enrichedProduct, [
              "idTipoPrecio",
              "IdTipoPrecio",
              "tipoDePrecio",
              "TipoDePrecio",
              "tipoCxa",
              "TipoCxa",
            ]),
          ),
          // Mismo criterio que idTipoProducto: si NR no trae idExterno para
          // este producto (pasa con vehículos que no aparecen en
          // /catalogs/producto-filtrado, ej. FRONTIER en este ambiente), se
          // deja vacío. Antes caía en defaultProductId (el id interno de
          // NR), lo que mandaba un AnzenProductoId falso a Anzen en vez de
          // bloquear la consulta con un mensaje claro.
          anzenProductoId: getProductExternalId(enrichedProduct),
          // Si NR no trae el tipo de producto, se queda vacío en vez de "1":
          // el flujo de Anzen debe bloquear hasta tener un valor real.
          idTipoProducto: safeString(getProductTypeId(enrichedProduct)),
          idBroker:
            getProductBrokerId(enrichedProduct) || "0",
          precioVehiculo:
            getProductPrice(enrichedProduct, quote.clasificacionId) ||
            getProductPrice(defaultProduct, quote.clasificacionId),
          planId: "",
          planNombre: "",
          paqueteExternoId: "",
          seguroAnzenKey: "",
          montoSeguro: "0",
        });
      } else if (!defaultProduct && !quote.productoId) {
        setLastError(
          "NR no regresó productos para esta combinación de marca/modelo/versión/año.",
        );
      }
    } finally {
      setBusy("productos", false);
    }
  }, [
    apiPostSafe,
    patchQuote,
    quote.agenciaId,
    quote.anio,
    quote.clasificacionId,
    quote.estrategiaId,
    quote.marcaId,
    quote.modeloId,
    quote.versionId,
    setBusy,
  ]);

  const loadPlanesYPaquetes = useCallback(async () => {
    if (!quote.productoId || !quote.estrategiaId) return;
    setBusy("planes", true);

    try {
      const [planesRaw, paquetesRaw] = await Promise.all([
        apiPostSafe(NR_ENDPOINTS.planes, {
          clasifId: Number(quote.clasificacionId),
          agenciaId: Number(quote.agenciaId || NR_DEFAULT_AGENCIA_ID),
          estrategiaId: Number(quote.estrategiaId),
          tipoOperacionId: Number(quote.tipoOperacionId || 1),
        }),
        apiPostSafe(NR_ENDPOINTS.paquetes, {
          idMarca: Number(quote.marcaId),
          destino: 1,
        }),
      ]);

      const planes = nrAsArray(planesRaw);
      const paquetes = nrAsArray(paquetesRaw);

      setCatalogs((prev) => ({
        ...prev,
        planes,
        paquetes,
      }));

      if (!quote.planId) {
        const defaultPackage = getDefaultCatalogItem(paquetes);
        const defaultPlan =
          (defaultPackage && findById(planes, nrGetId(defaultPackage))) ||
          getDefaultCatalogItem(planes) ||
          defaultPackage;

        if (defaultPlan) {
          const defaultPlanId = String(nrGetId(defaultPlan) || "");
          const packageMatch =
            findById(paquetes, defaultPlanId, ["idPaquete", "IdPaquete"]) ||
            defaultPlan;

          patchQuote({
            planId: defaultPlanId,
            planNombre: nrGetName(defaultPlan) || "",
            paqueteExternoId: resolvePackageExternalId(
              "",
              packageMatch,
              defaultPlanId,
            ),
            plazo: "",
            seguroAnzenKey: "",
            montoSeguro: "0",
          });
        }
      }
    } finally {
      setBusy("planes", false);
    }
  }, [
    apiPostSafe,
    patchQuote,
    quote.agenciaId,
    quote.clasificacionId,
    quote.estrategiaId,
    quote.marcaId,
    quote.productoId,
    quote.tipoOperacionId,
    setBusy,
  ]);

  const loadDatosPlan = useCallback(async () => {
    if (!quote.planId || !quote.productoId) return;
    setBusy("plan", true);

    try {
      const [plazosRaw, tiposSeguroRaw, segurosVidaRaw] = await Promise.all([
        apiPostSafe(NR_ENDPOINTS.plazos, {
          paqueteId: Number(quote.planId),
          productoId: Number(quote.productoId),
        }),
        apiPostSafe(NR_ENDPOINTS.tiposSeguro, { paqueteId: Number(quote.planId) }),
        apiPostSafe(NR_ENDPOINTS.segurosVida, {
          paqueteId: Number(quote.planId),
          perJuridId: Number(quote.personalidadJuridica || 1),
        }),
      ]);

      const plazos = nrAsArray(plazosRaw);
      const tiposSeguro = nrAsArray(tiposSeguroRaw);
      const segurosVida = nrAsArray(segurosVidaRaw);

      setCatalogs((prev) => ({
        ...prev,
        plazos,
        tiposSeguro,
        segurosVida,
      }));

      if (!quote.plazo && plazos.length) {
        const defaultTerm = getDefaultCatalogItem(plazos) || plazos[0];
        patchQuote({ plazo: String(getPlazoValue(defaultTerm) || "") });
      }

      if ((!quote.tipoSeguroId || quote.tipoSeguroId === "32") && tiposSeguro.length) {
        const defaultInsuranceType = getDefaultCatalogItem(tiposSeguro) || tiposSeguro[0];
        patchQuote({
          tipoSeguroId: String(nrGetId(defaultInsuranceType) || quote.tipoSeguroId || "32"),
          tipoSeguroNombre: nrGetName(defaultInsuranceType) || "",
        });
      }

      if (segurosVida.length) {
        const currentLifeInsurance = findById(
          segurosVida,
          quote.seguroVidaId,
          ["idTipoSeguroVida", "IdTipoSeguroVida"],
        );
        const defaultLifeInsurance =
          currentLifeInsurance ||
          getDefaultCatalogItem(segurosVida) ||
          segurosVida[0];

        patchQuote({
          seguroVidaId: safeString(
            nrGetId(defaultLifeInsurance, [
              "idTipoSeguroVida",
              "IdTipoSeguroVida",
            ]),
          ),
        });
      }
    } finally {
      setBusy("plan", false);
    }
  }, [
    apiPostSafe,
    patchQuote,
    quote.personalidadJuridica,
    quote.planId,
    quote.productoId,
    setBusy,
  ]);

  const loadAnzenCatalogs = useCallback(async () => {
    if (!quote.marcaId || !quote.planId || !quote.estadoId) return;
    setBusy("anzen", true);

    try {
      const anzenRaw = await apiPostSafe(
        NR_ENDPOINTS.anzenCatalogs,
        {
          BrandId: Number(quote.marcaId),
          OperationId: String(quote.tipoOperacionId || "1"),
          FiscalPersonalityId: quote.fiscalPersonalityId || "PF001",
          AnzenId: quote.anzenId || NR_ANZEN_ID,
          PlanId: String(quote.planId),
          StateId: Number(quote.estadoId),
        },
        null,
      );

      const anzenValue = anzenRaw || {};
      setCatalogs((prev) => ({ ...prev, anzen: anzenValue }));

      const insuranceOptions = nrAsArray(
        anzenValue.Insurances ||
          anzenValue.insurances ||
          anzenValue.Aseguradoras ||
          anzenValue.aseguradoras,
      );
      const coverageOptions = nrAsArray(
        anzenValue.TypeOfCoverages ||
          anzenValue.typeOfCoverages ||
          anzenValue.Coverages ||
          anzenValue.coverages,
      );
      const useOptions = nrAsArray(
        anzenValue.TypeOfUses ||
          anzenValue.typeOfUses ||
          anzenValue.Usos ||
          anzenValue.usos,
      );
      const udiOptionsLocal = nrAsArray(
        anzenValue.UdiLevels ||
          anzenValue.udiLevels ||
          anzenValue.Udis ||
          anzenValue.udis,
      );

      patchQuote((prev) => {
        const currentInsurance = findById(
          insuranceOptions,
          prev.aseguradoraId,
          ["insuranceId", "InsuranceId"],
        );
        const currentCoverage = findById(
          coverageOptions,
          prev.coberturaId,
          ["typeOfCoverageId", "TypeOfCoverageId"],
        );
        const currentUse = findById(
          useOptions,
          prev.tipoUsoId,
          [
            "typeOfUsesId",
            "TypeOfUsesId",
            "typeOfUseId",
            "TypeOfUseId",
          ],
        );
        const currentUdi = findById(
          udiOptionsLocal,
          prev.udiId,
          ["udiLevelId", "UdiLevelId"],
        );

        const insurance = currentInsurance
          ? null
          : getDefaultCatalogItem(insuranceOptions) ||
            insuranceOptions[0] ||
            null;
        const coverage = currentCoverage
          ? null
          : getDefaultCatalogItem(coverageOptions) ||
            coverageOptions[0] ||
            null;
        const use = currentUse
          ? null
          : getDefaultCatalogItem(useOptions) ||
            useOptions[0] ||
            null;
        const udi = currentUdi
          ? null
          : getDefaultCatalogItem(udiOptionsLocal) ||
            udiOptionsLocal[0] ||
            null;

        return {
          ...(insurance
            ? {
                aseguradoraId: safeString(
                  nrGetValue(insurance, [
                    "insuranceId",
                    "InsuranceId",
                    "id",
                    "Id",
                  ]),
                ),
                aseguradoraNombre: nrGetName(insurance),
              }
            : {}),
          ...(coverage
            ? {
                coberturaId: safeString(
                  nrGetValue(coverage, [
                    "typeOfCoverageId",
                    "TypeOfCoverageId",
                    "id",
                    "Id",
                  ]),
                ),
                coberturaNombre: nrGetName(coverage),
              }
            : {}),
          ...(use
            ? {
                tipoUsoId: safeString(
                  nrGetValue(use, [
                    "typeOfUsesId",
                    "TypeOfUsesId",
                    "typeOfUseId",
                    "TypeOfUseId",
                    "id",
                    "Id",
                  ]),
                ),
                tipoUsoNombre: nrGetName(use),
              }
            : {}),
          ...(udi
            ? {
                udiId: safeString(
                  nrGetValue(udi, ["udiLevelId", "UdiLevelId", "id", "Id"]),
                ),
                udiNombre: nrGetName(udi),
              }
            : {}),
        };
      });
    } finally {
      setBusy("anzen", false);
    }
  }, [
    apiPostSafe,
    patchQuote,
    quote.anzenId,
    quote.estadoId,
    quote.fiscalPersonalityId,
    quote.marcaId,
    quote.planId,
    quote.tipoOperacionId,
    setBusy,
  ]);

  useEffect(() => {
    loadBaseCatalogs();
  }, [loadBaseCatalogs]);

  useEffect(() => {
    loadAnios();
  }, [loadAnios]);

  useEffect(() => {
    loadMarcas();
  }, [loadMarcas]);

  useEffect(() => {
    loadModelos();
  }, [loadModelos]);

  useEffect(() => {
    loadVersiones();
  }, [loadVersiones]);

  useEffect(() => {
    loadEstrategiasYProductos();
  }, [loadEstrategiasYProductos]);

  useEffect(() => {
    loadPlanesYPaquetes();
  }, [loadPlanesYPaquetes]);

  useEffect(() => {
    loadDatosPlan();
  }, [loadDatosPlan]);

  useEffect(() => {
    loadAnzenCatalogs();
  }, [loadAnzenCatalogs]);

  const anzen = catalogs.anzen || {};
  const municipiosOptions = nrAsArray(
    anzen.Municipalities || anzen.municipalities || anzen.Municipios || anzen.municipios,
  );
  const aseguradorasOptions = nrAsArray(
    anzen.Insurances || anzen.insurances || anzen.Aseguradoras || anzen.aseguradoras,
  );
  const coberturasOptions = nrAsArray(
    anzen.TypeOfCoverages || anzen.typeOfCoverages || anzen.Coverages || anzen.coverages,
  );
  const usosOptions = nrAsArray(anzen.TypeOfUses || anzen.typeOfUses || anzen.Usos || anzen.usos);
  const udiOptions = nrAsArray(anzen.UdiLevels || anzen.udiLevels || anzen.Udis || anzen.udis);

  const selectedProduct = useMemo(
    () =>
      findById(
        catalogs.productosFiltrados,
        quote.productoId,
        ["idProducto", "IdProducto"],
      ) ||
      findById(catalogs.productos, quote.productoId, [
        "idProducto",
        "IdProducto",
      ]) ||
      null,
    [catalogs.productos, catalogs.productosFiltrados, quote.productoId],
  );

  const selectedPackage = useMemo(
    () =>
      findById(catalogs.paquetes, quote.planId, [
        "idPaquete",
        "IdPaquete",
      ]) ||
      findById(catalogs.planes, quote.planId, ["idPlan", "IdPlan"]) ||
      null,
    [catalogs.paquetes, catalogs.planes, quote.planId],
  );

  const anzenRequiredFields = useMemo(
    () => [
      ["Marca", quote.marcaId],
      ["Agencia externa", quote.agenciaClave],
      ["Producto Anzen", quote.anzenProductoId || getProductExternalId(selectedProduct)],
      [
        "Paquete externo",
        resolvePackageExternalId(
          quote.paqueteExternoId,
          selectedPackage,
          quote.planId,
        ),
      ],
      ["Plazo", quote.plazo],
      ["Estado", quote.estadoId],
      ["Municipio", quote.municipioId],
      ["Precio", quote.precioVehiculo],
      ["Tipo de seguro", quote.tipoSeguroId],
      ["Cobertura", quote.coberturaId],
      ["UDI", quote.udiId],
      ["Uso", quote.tipoUsoId],
      [
        // Este dato NO se selecciona en la UI: se lee del catálogo de
        // productos de NR (idTipoProducto). Si el producto elegido no lo
        // trae (pasa con /catalogs/productos para algunos vehículos, a
        // diferencia de /catalogs/producto-filtrado), este campo queda
        // vacío aunque el asesor sí haya seleccionado el vehículo.
        "Tipo de producto (dato de catálogo NR, no una selección)",
        quote.idTipoProducto || getProductTypeId(selectedProduct),
      ],
      [
        // Nombre CEBIP del PAQUETE (nombreCebip), no la estrategia del
        // vehículo. Depende de que selectedPackage ya haya resuelto contra
        // catalogs.paquetes; si el paquete tarda en cargar, este campo
        // queda vacío y debe bloquear la consulta en vez de mandar algo de
        // otro catálogo.
        "Estrategia (nombre CEBIP del paquete)",
        getPackageCebipName(selectedPackage),
      ],
    ],
    [
      quote.agenciaClave,
      quote.anzenProductoId,
      quote.coberturaId,
      quote.estadoId,
      quote.idTipoProducto,
      quote.marcaId,
      quote.municipioId,
      quote.paqueteExternoId,
      quote.planId,
      quote.plazo,
      quote.precioVehiculo,
      quote.productoId,
      quote.tipoSeguroId,
      quote.tipoUsoId,
      quote.udiId,
      selectedPackage,
      selectedProduct,
    ],
  );

  const missingAnzenFields = useMemo(
    () =>
      anzenRequiredFields
        .filter(([, value]) => !safeString(value))
        .map(([label]) => label),
    [anzenRequiredFields],
  );

  const canQueryAnzen = missingAnzenFields.length === 0;

  const buildAnzenPayload = useCallback(() => {
    if (!canQueryAnzen) {
      throw new Error(
        `Faltan datos para consultar seguros Anzen: ${missingAnzenFields.join(", ")}.`,
      );
    }

    const dynamicCatalogs = nrAsArray(
      anzen.DynamicCatalogs ||
        anzen.dynamicCatalogs ||
        anzen.ParametrosDinamicos ||
        anzen.parametrosDinamicos,
    );

    const anzenProductoId = safeString(
      quote.anzenProductoId ||
        getProductExternalId(selectedProduct) ||
        quote.productoId,
    );

    return {
      venta_id: quote.venta_id || "",
      // BUG probable encontrado: antes se mandaba el idCotizacion YA
      // GUARDADO en NR como IdCotReconsideracion, en cada consulta
      // posterior a Anzen. Ese campo es específicamente para reconsiderar
      // una cotización existente (doc: "ID de cotización para
      // reconsideración, 0 si no aplica"), no "el folio actual" — mandarle
      // un folio real fuera de un flujo de reconsideración probablemente
      // causaba el Anzen.GetQuotes.AccesoFalse que veíamos siempre después
      // de guardar. Un ejemplo real de NR que sí funcionó (accesso:true)
      // manda 0 fijo, igual que /cotiza siempre manda IdCotizacion=0.
      IdCotReconsideracion: 0,
      IdMarca: Number(quote.marcaId),
      IdAgenciaExterno: safeString(quote.agenciaClave),
      IdTipoOperacion: Number(quote.tipoOperacionId || 1),
      PersonalidadJuridica: Number(quote.personalidadJuridica || 1),
      AnzenProductoId: anzenProductoId,
      // PENDIENTE (ver nota junto a NR_ENDPOINTS): sigue sin haber una
      // fuente confirmada para el IdPaqueteExterno real de Anzen; se usa
      // el cálculo normal y Anzen puede seguir regresando AccesoFalse
      // hasta que NR confirme de dónde debe salir este dato.
      IdPaqueteExterno: Number(
        resolvePackageExternalId(
          quote.paqueteExternoId,
          selectedPackage,
          quote.planId,
        ),
      ),
      Plazo: Number(quote.plazo),
      IdEstado: Number(quote.estadoId),
      IdMunicipio: Number(quote.municipioId),
      Precio: toNumber(quote.precioVehiculo),
      TotalAccesorios: toNumber(quote.accesoriosFinanciados),
      TipoSeguro: Number(quote.tipoSeguroId || 0),
      TypeOfCoverageId: Number(quote.coberturaId || 0),
      UdiLevelId: Number(quote.udiId || 0),
      GpsId: Number(quote.gpsId || 0),
      // Ya validado como requerido en anzenRequiredFields; no se adivina.
      TipoUso: Number(quote.tipoUsoId),
      IdTipoProducto: Number(
        quote.idTipoProducto || getProductTypeId(selectedProduct),
      ),
      // BUG probable encontrado: antes caía en quote.estrategiaNombre
      // (nombre de la ESTRATEGIA DEL VEHÍCULO, catálogo /catalogs/estrategias
      // — ej. "PRECIO DE LISTA") cuando selectedPackage aún no resolvía. Es
      // un catálogo completamente distinto al nombreCebip del PAQUETE que
      // espera Anzen (ej. "Portal Nissan"). Con datos reales: mandar el
      // nombreCebip correcto SÍ dio acceso a Anzen; mandar la estrategia
      // del vehículo dio AccesoFalse. Ya no se adivina con esos fallbacks:
      // si selectedPackage no resolvió nombreCebip todavía, se manda vacío
      // (canQueryAnzen debe bloquear la consulta en vez de mandar un valor
      // de otro catálogo que parece plausible pero no lo es).
      Estrategia: getPackageCebipName(selectedPackage),
      ParametrosDinamicos: dynamicCatalogs
        .map((item) => ({
          IdTypeCat: Number(
            nrGetValue(item, ["idTypeCat", "IdTypeCat", "id", "Id"]),
          ),
          TypeCat: safeString(
            nrGetValue(item, ["typeCat", "TypeCat", "nombre", "Nombre"]),
          ),
          IdValue: safeString(
            nrGetValue(item, ["idValue", "IdValue", "valor", "Valor"]),
          ),
        }))
        .filter((item) => item.IdTypeCat && item.IdValue),
    };
  }, [
    anzen,
    canQueryAnzen,
    missingAnzenFields,
    quote.accesoriosFinanciados,
    quote.agenciaClave,
    quote.anzenProductoId,
    quote.coberturaId,
    quote.estadoId,
    quote.gpsId,
    quote.idTipoProducto,
    quote.marcaId,
    quote.municipioId,
    quote.paqueteExternoId,
    quote.personalidadJuridica,
    quote.planId,
    quote.planNombre,
    quote.plazo,
    quote.precioVehiculo,
    quote.productoId,
    quote.tipoOperacionId,
    quote.tipoSeguroId,
    quote.tipoUsoId,
    quote.udiId,
    quote.venta_id,
    quote.estrategiaNombre,
    selectedPackage,
    selectedProduct,
  ]);

  const handleConsultarSegurosAnzen = useCallback(
    async ({ silent = false } = {}) => {
      try {
        const payload = buildAnzenPayload();
        setBusy("segurosAnzen", true);
        setAnzenError(null);

        const data = await apiPost(
          NR_ENDPOINTS.consultarSegurosAnzen,
          payload,
        );
        const response = getAnzenResponseObject(data);
        const accessValue = safeString(
          nrGetValue(response, ["accesso", "Accesso", "acceso", "Acceso"]),
        ).toLowerCase();

        if (accessValue === "false" || accessValue === "0") {
          throw new Error(
            safeString(
              nrGetValue(response, [
                "mensaje",
                "Mensaje",
                "message",
                "Message",
              ]),
            ) || "Anzen respondió sin acceso válido.",
          );
        }

        const options = normalizeAnzenInsuranceOptions(response);
        setAnzenCotizacion(response);
        setAnzenError(null);

        if (options.length) {
          patchQuote((prev) => {
            const currentOption =
              options.find(
                (item) =>
                  safeString(item.__key) ===
                  safeString(prev.seguroAnzenKey),
              ) || options[0];

            return buildInsurancePatch(currentOption, response);
          });
        } else {
          // Anzen respondió pero sin opciones: se limpia el rastreo de la
          // selección de Anzen, pero NO se toca montoSeguro. Si el asesor
          // ya había capturado un monto manual, debe conservarse.
          patchQuote(buildInsurancePatch(null, response));
        }

        if (!silent) {
          await Swal.fire({
            icon: options.length ? "success" : "info",
            title: options.length
              ? "Seguros Anzen actualizados"
              : "Sin seguros disponibles",
            text: options.length
              ? `Se encontraron ${options.length} opción(es) de seguro.`
              : "Anzen respondió correctamente, pero no regresó opciones para esta combinación.",
            timer: 1800,
            showConfirmButton: false,
          });
        }

        return response;
      } catch (error) {
        const errorInfo = {
          message: error.message || String(error),
          code: safeString(error.code),
          status: Number(error.status || 0),
        };

        setAnzenCotizacion(null);
        setAnzenError(errorInfo);
        // Igual que arriba: un error de Anzen (ej. AccesoFalse) no invalida
        // un monto de seguro capturado manualmente por el asesor. Antes esto
        // lo ponía en "0" cada vez que la consulta automática fallaba,
        // borrando silenciosamente lo que el asesor había escrito.
        patchQuote(buildInsurancePatch(null, null));

        if (!silent) {
          setLastError(errorInfo.message);
          await Swal.fire({
            icon: "error",
            title: "No se pudieron consultar seguros",
            text: errorInfo.code
              ? `${errorInfo.message} (${errorInfo.code})`
              : errorInfo.message,
          });
        } else {
          console.warn("No se pudieron consultar seguros Anzen:", error);
        }
        return null;
      } finally {
        setBusy("segurosAnzen", false);
      }
    },
    [
      apiPost,
      buildAnzenPayload,
      patchQuote,
      setBusy,
    ],
  );

  const anzenRequestSignature = useMemo(
    () =>
      JSON.stringify(
        anzenRequiredFields.map(([label, value]) => [
          label,
          safeString(value),
        ]),
      ),
    [anzenRequiredFields],
  );

  useEffect(() => {
    if (!canQueryAnzen) {
      setAnzenCotizacion(null);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      handleConsultarSegurosAnzen({ silent: true });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [
    anzenRequestSignature,
    canQueryAnzen,
    handleConsultarSegurosAnzen,
  ]);

  const buildCotizaPayload = useCallback(() => {
    const precio = toNumber(quote.precioVehiculo);
    const enganche = toNumber(quote.enganche) || Math.round((precio * toNumber(quote.porcentajeEnganche)) / 100);
    const porcentajeEnganche = precio > 0 ? Math.round((enganche / precio) * 10000) / 100 : 0;
    const montoSeguro = toNumber(quote.montoSeguro);
    // Se envía 0 para que Procotiza calcule la CxA configurada en el paquete.
    const comisionApertura = 0;
    const anzenResponse = getAnzenResponseObject(anzenCotizacion);
    const seguroPromocion = toNumber(
      nrGetValue(anzenResponse, ["seguroPromocion", "SeguroPromocion"]),
    );
    const permiteSeguroPromocion = Number(
      nrGetValue(anzenResponse, [
        "permiteSeguroPromocion",
        "PermiteSeguroPromocion",
      ]) || 0,
    );
    const seguroGratis =
      quote.seguroAnzenTipo === "free" ||
      Boolean(anzenResponse.insuranceFree || anzenResponse.InsuranceFree)
        ? 1
        : 0;

    if (!quote.productoId || !quote.planId || !quote.plazo || !precio) {
      throw new Error("Completa producto NR, plan NR, plazo y precio antes de cotizar.");
    }

    return {
      venta_id: quote.venta_id || "",
      IdPaquete: Number(quote.planId),
      IdPerJuridica: Number(quote.idPerJuridica || 14),
      IdTipoOperacion: Number(quote.tipoOperacionId || 1),
      Plazo: Number(quote.plazo),
      IdClasificacion: Number(quote.clasificacionId || 63),
      IdVersion: Number(quote.versionId || 0),
      IdProducto: Number(quote.productoId),
      IdTipoUnidad: 0,
      // Constante fija del protocolo NR (no un default adivinado): la doc de
      // /cotiza solo documenta el valor 0=particular para "Uso" y no expone
      // catálogo alguno para este campo. No usar quote.tipoUsoId aquí: ese
      // campo trae el TypeOfUsesId del catálogo dinámico de Anzen, que es un
      // catálogo distinto y no intercambiable con este.
      // ⚠️ PENDIENTE DE CONFIRMAR CON NR: un ejemplo real de guardar-cotizacion
      // que compartió NR trae "tipoUso": 1, no 0. Eso significa que el enum
      // completo de este campo no está documentado (hay al menos un valor más
      // aparte de 0). Se deja en 0 porque es el único valor documentado y no
      // sabemos qué distingue 0 de 1 (no depende de nada que ya capturemos:
      // no es PF/PM, no es Anzen). No cambiar a 1 sin que NR confirme la regla.
      Uso: 0,
      Unidades: 1,
      IdTasaIva: 2,
      Precio: precio,
      Enganche: enganche,
      PorcentajeEnganche: porcentajeEnganche,
      Bandera: Number(quote.tipoOperacionId || 1) === 3 ? 1 : 0,
      MontoAccesorios: toNumber(quote.accesoriosFinanciados),
      MontoAccesoriosContado: toNumber(quote.accesoriosContado),
      // Asunción: la UI aún no distingue accesorios con/sin ISAN, por lo que
      // el total financiado se manda completo como "sin ISAN". Si en algún
      // momento se vende un accesorio con ISAN, este reparto debe actualizarse
      // o el cálculo de Procotiza quedará mal sin que se note en pantalla.
      MontoAccesoriosISAN: 0,
      MontoAccesoriosNoISAN: toNumber(quote.accesoriosFinanciados),
      IdTipoSeguro: Number(quote.tipoSeguroId || 32),
      Cobertura: Number(quote.coberturaId || 0),
      IdAseguradora: Number(quote.aseguradoraId || 0),
      IdEstado: Number(quote.estadoId || 0),
      MontoSeguroProducto: quote.tipoCompraSeguro === "Financiado" ? montoSeguro : 0,
      MontoSeguroGAP: 0,
      MontoSeguroVida: 0,
      Derecho: 0,
      IdBroker: Number(quote.idBroker || getProductBrokerId(selectedProduct) || 0),
      ParamsBroker: "",
      // BUG corregido: quote.seguroVidaId es el id del catálogo real
      // /catalogs/seguros-vida (ej. 1=Seguro Temporal, 2=Seguro Vitalicio),
      // NO el mismo valor que espera este campo. La doc de /cotiza dice
      // "TipoSeguroVida: 0=ninguno, 227=solo vida", así que se deriva del
      // hecho de que exista o no una selección del catálogo, en vez de
      // reenviar el id del catálogo (que antes se enviaba tal cual, incluso
      // con el fallback "227" cuando en realidad 227 nunca es un id válido
      // de ese catálogo).
      TipoSeguroVida: safeString(quote.seguroVidaId) ? 227 : 0,
      DestinoSubsidio: 2,
      SubsidioAdicional: 0,
      DescuentoAdicional: 0,
      SubsidioMarca: 0,
      SubsidioAgencia: 0,
      SubsidioFinanciera: 0,
      SubsidioAdicionalExtra: 0,
      TipoSubsidioAdicional: 327,
      ServiciosFinanciados: 0,
      ServiciosNoFinanciados: 0,
      Apparel: 0,
      TotalProductosAdicionales: toNumber(quote.productosAdicionales),
      TotalGarantiaExtendida: toNumber(quote.garantiaExtendida),
      SeguroGratis: seguroGratis,
      MontoAnualAnzen:
        toNumber(quote.seguroAnzenTotalAmount) || montoSeguro,
      ComisionApertura: comisionApertura,
      TipoCxA: 331,
      ValorTasaEdit: toNumber(quote.tasaEditadaNR),
      DiaPago: 1,
      MtoGarantia: 1,
      PagoFinalManual: 0,
      TabuladorSeguro: "",
      DatosTabulador: "",
      TabuladorAccesorio: "",
      RentasDeposito: 0,
      PlazoModificador: "",
      // Para recalcular siempre con los datos actuales del formulario.
      // El folio guardado se utiliza únicamente para generar el PDF.
      IdCotizacion: 0,
      IdCotReconsideracion: 0,
      PermiteSeguroPromocion: permiteSeguroPromocion,
      SeguroPromocion: seguroPromocion,
    };
  }, [
    anzenCotizacion,
    nrCotizacion,
    quote,
    selectedProduct,
  ]);

  const buildGuardarPayload = useCallback(
    (cotizacionOverride = null) => {
      const cotizaPayload = buildCotizaPayload();

      // Debe venir del catálogo real de NR (producto seleccionado). Sin él
      // no se puede armar "tipoDePrecio" y NR rechazaría la operación de
      // todos modos; mejor cortar aquí con un mensaje claro que enviar un
      // valor inventado.
      if (!safeString(quote.productoTipoPrecioId)) {
        throw new Error(
          "Tipo de precio del producto no disponible: NR no lo regresó para este producto. No se puede guardar la cotización.",
        );
      }

      const cotizacionParaGuardar = cotizacionOverride || nrCotizacion;
      const listaAccesorios = [];
      const anzenResponse = getAnzenResponseObject(anzenCotizacion);
      const selectedInsurance =
        selectedAnzenInsurance ||
        normalizeAnzenInsuranceOptions(anzenResponse)[0] ||
        null;

      const selectedInsuranceId = Number(
        selectedInsurance?.__insuranceId || quote.seguroAnzenId || 0,
      );
      const selectedCampaignId = Number(
        selectedInsurance?.__campaignId ||
          quote.seguroAnzenCampaniaId ||
          0,
      );
      const selectedAmount =
        toNumber(selectedInsurance?.__amount) ||
        toNumber(quote.seguroAnzenAmount) ||
        toNumber(quote.montoSeguro);
      const selectedTotalAmount =
        toNumber(selectedInsurance?.__totalAmount) ||
        toNumber(quote.seguroAnzenTotalAmount) ||
        toNumber(quote.montoSeguro);
      const selectedSurcharges =
        toNumber(selectedInsurance?.__surcharges) ||
        toNumber(quote.seguroAnzenSurcharges);

      const insuranceFree =
        anzenResponse.insuranceFree || anzenResponse.InsuranceFree || null;
      const insurancePromotional =
        anzenResponse.insurancePromotional ||
        anzenResponse.InsurancePromotional ||
        null;
      const extendedWarranty = nrAsArray(
        anzenResponse.extendedWarranty ||
          anzenResponse.ExtendedWarranty,
      );
      const additionalProducts = nrAsArray(
        anzenResponse.additionalProducts ||
          anzenResponse.AdditionalProducts,
      );

      const normalizeInsuranceObject = (item, promotional = false) => {
        if (!item || typeof item !== "object") return null;

        return {
          insuranceId: Number(
            nrGetValue(item, ["insuranceId", "InsuranceId"]) || 0,
          ),
          campaignId: Number(
            nrGetValue(item, ["campaignId", "CampaignId"]) || 0,
          ),
          amount: toNumber(nrGetValue(item, ["amount", "Amount"])),
          totalAmount: toNumber(
            nrGetValue(item, ["totalAmount", "TotalAmount"]),
          ),
          ...(promotional
            ? {
                promoAmount: toNumber(
                  nrGetValue(item, ["promoAmount", "PromoAmount"]),
                ),
              }
            : {}),
          surcharges: toNumber(
            nrGetValue(item, ["surcharges", "Surcharges"]),
          ),
        };
      };

      if (toNumber(quote.accesoriosFinanciados) > 0) {
        listaAccesorios.push({
          idAccesorio: 0,
          tipo: 1,
          monto: toNumber(quote.accesoriosFinanciados),
        });
      }

      if (toNumber(quote.accesoriosContado) > 0) {
        listaAccesorios.push({
          idAccesorio: 0,
          tipo: 2,
          monto: toNumber(quote.accesoriosContado),
        });
      }

      // La tasa enviada al servicio de guardado sale de la respuesta de /cotiza.
      // Si Procotiza devuelve tasa 0, se conserva 0; no se sustituye por tasaEditada.
      const tasaGuardar = getNrRate(cotizacionParaGuardar);

      const tieneDatosAnzen =
        selectedInsuranceId > 0 ||
        Boolean(insuranceFree) ||
        Boolean(insurancePromotional) ||
        extendedWarranty.length > 0 ||
        additionalProducts.length > 0;

      const anzenDataPayload =
        NR_INCLUDE_ANZEN_DATA_ON_SAVE && tieneDatosAnzen
          ? {
              idMarca: Number(quote.marcaId || 0),
              anzenProductoId:
                quote.anzenProductoId ||
                getProductExternalId(selectedProduct) ||
                quote.productoId ||
                0,
              estrategia: getPackageCebipName(selectedPackage),
              usuarioRegistro: Number(
                client.user_id || localStorage.getItem("userId") || 0,
              ),
              idCotReconsideracion: 0,
              insurances:
                selectedInsuranceId &&
                selectedInsurance?.__kind === "standard"
                  ? [
                      {
                        insuranceId: selectedInsuranceId,
                        campaignId: selectedCampaignId,
                        amount: selectedAmount,
                        surcharges: selectedSurcharges,
                      },
                    ]
                  : [],
              insuranceFree: normalizeInsuranceObject(insuranceFree),
              insurancePromotional: normalizeInsuranceObject(
                insurancePromotional,
                true,
              ),
              extendedWarranty: extendedWarranty.map((item) => ({
                subProductId: Number(
                  nrGetValue(item, ["subProductId", "SubProductId"]) || 0,
                ),
                baseAmount: toNumber(
                  nrGetValue(item, ["baseAmount", "BaseAmount"]),
                ),
                finalAmount: toNumber(
                  nrGetValue(item, ["finalAmount", "FinalAmount"]),
                ),
                type: safeString(nrGetValue(item, ["type", "Type"])),
              })),
              additionalProducts: additionalProducts.map((item) => ({
                subBranchId: safeString(
                  nrGetValue(item, ["subBranchId", "SubBranchId"]),
                ),
                subProductId: safeString(
                  nrGetValue(item, ["subProductId", "SubProductId"]),
                ),
                amount: toNumber(nrGetValue(item, ["amount", "Amount"])),
                type: safeString(nrGetValue(item, ["type", "Type"])),
              })),
            }
          : null;

      return {
        venta_id: quote.venta_id || "",
        idPaquete: String(quote.planId || cotizaPayload.IdPaquete),
        idProducto: Number(quote.productoId || cotizaPayload.IdProducto),
        plazo: Number(cotizaPayload.Plazo),
        enganche: Number(cotizaPayload.Enganche),
        tasa: tasaGuardar,
        moneda: 1,
        diaPago: 1,
        agencia: safeString(
          quote.agenciaClave || NR_DEFAULT_AGENCIA_CLAVE,
        ),
        // Constante fija del protocolo NR (ver Uso en /cotiza). No usar
        // quote.tipoUsoId (catálogo Anzen).
        // ⚠️ Mismo pendiente que en /cotiza: un ejemplo real de NR trae
        // tipoUso=1. Ver comentario detallado junto a "Uso" en buildCotizaPayload.
        tipoUso: 0,
        unidadesProducto: 1,
        // OJO: la doc NR v1.1 dice personalidadJuridica = 1 (PF) / 2 (PM),
        // pero validado en UAT este endpoint acepta 14/15, igual que
        // IdPerJuridica en /cotiza. Si NR corrige su documentación o cambia
        // el comportamiento, este mapeo debe revisarse (no usar quote.personalidadJuridica
        // aquí, que es la escala 1/2 que sí usa Anzen).
        personalidadJuridica: Number(quote.idPerJuridica || 14),
        // Debe venir del catálogo real de NR (ver loadEstrategiasYProductos).
        // Si está vacío, buildGuardarPayload ya lo rechazó arriba con un
        // error explícito antes de llegar aquí.
        tipoDePrecio: Number(quote.productoTipoPrecioId),
        seguro: {
          tipoSeguro: Number(quote.tipoSeguroId || 32),
          estado: quote.estadoNombre || "",
          municipio: quote.municipioNombre || "",
          // Mismo bug corregido que en /cotiza: quote.seguroVidaId es el id
          // del catálogo /catalogs/seguros-vida, no el flag 0/227 que espera
          // este campo (doc: "seguroVida int, 0=ninguno").
          seguroVida: safeString(quote.seguroVidaId) ? 227 : 0,
          monto: toNumber(quote.montoSeguro),
          idAseguradora: Number(quote.aseguradoraId || 0),
          aseguradora: quote.aseguradoraNombre || "",
          seguroGratis:
            selectedInsurance?.__kind === "free" || insuranceFree ? 1 : 0,
          tipoUso: quote.tipoUsoNombre || "",
          cobertura: quote.coberturaNombre || "",
          nivelUdi: safeString(quote.udiId || "0"),
          exclusivoPickup: "0",
          montoTotal: selectedTotalAmount,
          idCapania: selectedCampaignId,
          // idInsurance pertenece a la opción regresada por Anzen.
          // No debe reutilizarse el idAseguradora cuando Anzen no respondió.
          idInsurance: selectedInsuranceId > 0 ? selectedInsuranceId : 0,
          idCampaing: selectedCampaignId,
          descripcionSG: "",
          campaingDesc: "",
          campainDescSG: "",
          termSG: 0,
          descCobertura: quote.coberturaNombre || "",
          descUso: quote.tipoUsoNombre || "",
          surcharges: selectedSurcharges,
          policy: selectedAmount,
          policyFree: insuranceFree
            ? toNumber(
                nrGetValue(insuranceFree, [
                  "amount",
                  "Amount",
                  "totalAmount",
                  "TotalAmount",
                ]),
              )
            : 0,
          subproducto: 0,
          subproductoSG: 0,
          insuranceProg: 0,
          insuranceVers: 0,
          insuranceProgSG: 0,
          insuranceVersSG: 0,
          coberturaGen: quote.coberturaNombre || "",
          coberturaGenFree: "",
          totalAmountSG: toNumber(
            nrGetValue(anzenResponse, [
              "totalAmountSg",
              "TotalAmountSg",
            ]),
          ),
        },
        listaAccesorios,
        // El servicio probado acepta null cuando no hay garantía extendida.
        gEX:
          toNumber(quote.garantiaExtendida) > 0
            ? {
                montoBase: toNumber(quote.garantiaExtendida),
                overPricing: 0,
                plazo: Number(quote.plazo || 0),
                subproductoId: 0,
                tipo: "Garantía Extendida",
                insuranceIdEW: 0,
              }
            : null,
        // BUG corregido: antes se mandaba [] siempre, aunque el asesor
        // hubiera capturado un monto en "Productos adicionales" (el mismo
        // monto que sí se le manda a /cotiza como TotalProductosAdicionales
        // y que sí afecta pagoProductosAdic/pagoMensualTotal). Sin catálogo
        // específico de producto adicional, se manda como un solo renglón
        // genérico con el monto capturado, igual que se hace con gEX.
        productosAdicionales:
          toNumber(quote.productosAdicionales) > 0
            ? [
                {
                  subproductoId: 0,
                  productoId: 0,
                  term: Number(quote.plazo || 0),
                  insuranceAPId: 0,
                  provider: 0,
                  subBranchId: "",
                  monto: toNumber(quote.productosAdicionales),
                  policy: 0,
                  descripcion: "Producto adicional",
                  typeSeguro: "",
                  especifications: "",
                },
              ]
            : [],
        pagosEspeciales: null,
        // Por defecto se conserva null, igual que en el request validado.
        // Puede habilitarse desde .env cuando NR confirme que requiere el objeto.
        anzenData: anzenDataPayload,
      };
    },
    [
      anzenCotizacion,
      buildCotizaPayload,
      client.user_id,
      nrCotizacion,
      quote,
      selectedAnzenInsurance,
      selectedPackage,
      selectedProduct,
    ],
  );

  const validateBeforeNr = useCallback(() => {
    const required = [
      [quote.clasificacionId, "Clasificación"],
      [quote.marcaId, "Marca NR"],
      [quote.modeloId, "Modelo NR"],
      [quote.anio, "Año"],
      [quote.versionId, "Versión NR"],
      [quote.productoId, "Producto NR"],
      [quote.planId, "Plan / paquete NR"],
      [quote.plazo, "Plazo"],
      [quote.precioVehiculo, "Precio del vehículo"],
    ];

    const missing = required.filter(([value]) => !safeString(value)).map(([, label]) => label);

    if (missing.length) {
      throw new Error(`Faltan datos para cotizar: ${missing.join(", ")}.`);
    }
  }, [quote]);

  const handleCotizarNR = useCallback(async () => {
    try {
      validateBeforeNr();
      setBusy("cotiza", true);
      setLastError("");

      const payload = buildCotizaPayload();
      const data = await apiPost(NR_ENDPOINTS.cotiza, payload);
      const cotizacion = nrFirstObject(data) || data;

      setLastCotizaRequest(payload);
      setLastCotizaSignature(currentQuoteSignature);
      setNrCotizacion(cotizacion);
      setPdfBase64("");
      setPdfPreviewOpen(false);

      await Swal.fire({
        icon: "success",
        title: "Cotización NR calculada",
        text: "Ya se muestra la mensualidad oficial de NR.",
        timer: 1600,
        showConfirmButton: false,
      });

      return cotizacion;
    } catch (error) {
      setLastError(error.message || String(error));
      Swal.fire({ icon: "error", title: "No se pudo cotizar", text: error.message });
      return null;
    } finally {
      setBusy("cotiza", false);
    }
  }, [
    apiPost,
    buildCotizaPayload,
    currentQuoteSignature,
    setBusy,
    validateBeforeNr,
  ]);

  const handleGuardarNR = useCallback(async () => {
    try {
      validateBeforeNr();
      setBusy("guardar", true);
      setLastError("");

      const cotizaPayload = buildCotizaPayload();
      let cotizacionParaGuardar = nrCotizacion;

      if (
        !cotizacionParaGuardar ||
        lastCotizaSignature !== currentQuoteSignature
      ) {
        const dataCotiza = await apiPost(
          NR_ENDPOINTS.cotiza,
          cotizaPayload,
        );

        cotizacionParaGuardar = nrFirstObject(dataCotiza) || dataCotiza;
        setLastCotizaRequest(cotizaPayload);
        setLastCotizaSignature(currentQuoteSignature);
        setNrCotizacion(cotizacionParaGuardar);
      }

      const data = await apiPost(
        NR_ENDPOINTS.guardarCotizacion,
        buildGuardarPayload(cotizacionParaGuardar),
      );
      const id =
        data?.idCotizacion ||
        data?.IdCotizacion ||
        nrFirstObject(data)?.idCotizacion ||
        nrFirstObject(data)?.IdCotizacion ||
        "";

      const nrSaveMessage = safeString(
        data?.log?.respuesta ||
          data?.Log?.Respuesta ||
          data?.respuesta ||
          data?.Respuesta ||
          nrFirstObject(data)?.log?.respuesta ||
          nrFirstObject(data)?.Log?.Respuesta,
      );

      if (!id || Number(id) <= 0) {
        throw new Error(
          nrSaveMessage ||
            "NR no pudo guardar la cotización y no regresó un IdCotizacion válido.",
        );
      }

      setIdCotizacion(String(id));
      setLastSavedSignature(currentQuoteSignature);
      setPdfBase64("");
      setPdfPreviewOpen(false);
      patchQuote({ venta_id: quote.venta_id || "" });

      await Swal.fire({
        icon: "success",
        title: "Cotización NR guardada",
        text: `ID de cotización: ${id}`,
        timer: 1700,
        showConfirmButton: false,
      });

      return String(id);
    } catch (error) {
      setLastError(error.message || String(error));
      Swal.fire({ icon: "error", title: "No se pudo guardar NR", text: error.message });
      return "";
    } finally {
      setBusy("guardar", false);
    }
  }, [
    apiPost,
    buildCotizaPayload,
    buildGuardarPayload,
    currentQuoteSignature,
    lastCotizaSignature,
    nrCotizacion,
    patchQuote,
    quote.venta_id,
    setBusy,
    validateBeforeNr,
  ]);

  const handleGenerarPdfNR = useCallback(async () => {
    try {
      setBusy("pdf", true);
      setLastError("");

      let id = idCotizacion;

      if (!id || lastSavedSignature !== currentQuoteSignature) {
        id = await handleGuardarNR();
      }

      if (!id) return;

      const data = await apiPost(NR_ENDPOINTS.generarPdf, {
        venta_id: quote.venta_id || "",
        IdCotizacion: Number(id),
      });

      const base64 =
        data?.pdfBase64 ||
        data?.PdfBase64 ||
        data?.PDFBase64 ||
        nrFirstObject(data)?.pdfBase64 ||
        nrFirstObject(data)?.PdfBase64 ||
        "";

      if (!base64) throw new Error("NR respondió, pero no regresó PDF Base64.");

      setPdfBase64(base64);

      await Swal.fire({
        icon: "success",
        title: "PDF NR generado",
        text: "Ya puedes verlo o descargarlo.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      setLastError(error.message || String(error));
      Swal.fire({ icon: "error", title: "No se pudo generar PDF", text: error.message });
    } finally {
      setBusy("pdf", false);
    }
  }, [
    apiPost,
    currentQuoteSignature,
    handleGuardarNR,
    idCotizacion,
    lastSavedSignature,
    quote.venta_id,
    setBusy,
  ]);

  const handleOpenPdf = useCallback(() => {
    if (!pdfObjectUrl) {
      Swal.fire({
        icon: "info",
        title: "PDF pendiente",
        text: "Primero genera el PDF NR.",
      });
      return;
    }

    setPdfPreviewOpen(true);
  }, [pdfObjectUrl]);

  const handleDownloadPdf = useCallback(() => {
    if (!pdfObjectUrl) {
      Swal.fire({
        icon: "info",
        title: "PDF pendiente",
        text: "Primero genera el PDF NR.",
      });
      return;
    }

    const link = document.createElement("a");
    link.href = pdfObjectUrl;
    link.download = `Cotizacion_NR_${quote.folio_venta || idCotizacion || "propuesta"}.pdf`;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [idCotizacion, pdfObjectUrl, quote.folio_venta]);

  const handleReset = useCallback(() => {
    setClient({
      ...EMPTY_CLIENT,
      asesor: localStorage.getItem("userName") || localStorage.getItem("nombre") || "",
      user_id: localStorage.getItem("userId") || "",
    });
    setQuote(EMPTY_QUOTE);
    setCatalogs((prev) => ({ ...EMPTY_CATALOGS, clasificaciones: prev.clasificaciones, estados: prev.estados, anios: prev.anios }));
    setNrCotizacion(null);
    setAnzenCotizacion(null);
    setAnzenError(null);
    setIdCotizacion("");
    setLastCotizaRequest(null);
    setLastCotizaSignature("");
    setLastSavedSignature("");
    setPdfBase64("");
    setPdfPreviewOpen(false);
    setLastError("");
    setRawResponseOpen(false);
  }, []);

  const selectedResumen = useMemo(
    () => ({
      cliente: client.nombre || "Cliente pendiente",
      vehiculo: [quote.marcaNombre, quote.modeloNombre, quote.versionNombre, quote.anio].filter(Boolean).join(" ") || "Vehículo pendiente",
      producto: quote.productoNombre || "Producto NR pendiente",
      plan: quote.planNombre || "Plan NR pendiente",
      plazo: quote.plazo ? `${quote.plazo} meses` : "Plazo pendiente",
      precio: formatCurrency(quote.precioVehiculo),
      enganche: formatCurrency(quote.enganche),
      mensualidadNR: nrMensualidad ? formatCurrency(nrMensualidad) : "Pendiente de cotizar",
      idCotizacion: isCurrentQuoteSaved
        ? idCotizacion
        : idCotizacion
          ? "Cambios sin guardar"
          : "Pendiente de guardar",
    }),
    [client.nombre, idCotizacion, isCurrentQuoteSaved, nrMensualidad, quote],
  );


  const requiredQuoteFields = useMemo(
    () => [
      { label: "Clasificación", value: quote.clasificacionId, stepId: "nr-step-fiscal" },
      { label: "Marca NR", value: quote.marcaId, stepId: "nr-step-vehicle" },
      { label: "Modelo NR", value: quote.modeloId, stepId: "nr-step-vehicle" },
      { label: "Año", value: quote.anio, stepId: "nr-step-vehicle" },
      { label: "Versión NR", value: quote.versionId, stepId: "nr-step-vehicle" },
      { label: "Producto NR", value: quote.productoId, stepId: "nr-step-vehicle" },
      { label: "Plan / paquete NR", value: quote.planId, stepId: "nr-step-plan" },
      { label: "Plazo", value: quote.plazo, stepId: "nr-step-plan" },
      { label: "Precio del vehículo", value: quote.precioVehiculo, stepId: "nr-step-amounts" },
    ],
    [quote],
  );

  const missingRequiredFields = useMemo(
    () => requiredQuoteFields.filter((field) => !safeString(field.value)),
    [requiredQuoteFields],
  );

  const flowSteps = useMemo(
    () => [
      {
        id: "nr-step-client",
        label: "Cliente",
        shortLabel: "Cliente",
        done: true,
      },
      {
        id: "nr-step-fiscal",
        label: "Operación",
        shortLabel: "Operación",
        done: Boolean(safeString(quote.clasificacionId) && safeString(quote.tipoOperacionId) && safeString(quote.agenciaId)),
      },
      {
        id: "nr-step-vehicle",
        label: "Vehículo",
        shortLabel: "Vehículo",
        done: Boolean(
          safeString(quote.marcaId) &&
            safeString(quote.modeloId) &&
            safeString(quote.anio) &&
            safeString(quote.versionId) &&
            safeString(quote.productoId),
        ),
      },
      {
        id: "nr-step-plan",
        label: "Plan y seguro",
        shortLabel: "Plan",
        done: Boolean(safeString(quote.planId) && safeString(quote.plazo)),
      },
      {
        id: "nr-step-amounts",
        label: "Montos",
        shortLabel: "Montos",
        done: Boolean(toNumber(quote.precioVehiculo) > 0),
      },
      {
        id: "nr-step-result",
        label: "Resultado",
        shortLabel: "Resultado",
        done: Boolean(nrCotizacion),
      },
    ],
    [nrCotizacion, quote],
  );

  const completedSteps = flowSteps.filter((step) => step.done).length;
  const progressPercentage = Math.round((completedSteps / flowSteps.length) * 100);
  const isQuoteReady = missingRequiredFields.length === 0;
  const catalogLoading = loading.marcas || loading.modelos || loading.versiones || loading.productos;
  const planLoading = loading.planes || loading.plan || loading.anzen;
  const firstPendingStep = flowSteps.find((step) => !step.done);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleDownPaymentChange = useCallback(
    (value) => {
      const price = toNumber(quote.precioVehiculo);
      const downPayment = toNumber(value);
      const percentage = price > 0 ? Math.min(100, Math.round((downPayment / price) * 10000) / 100) : toNumber(quote.porcentajeEnganche);
      patchQuote({ enganche: value, porcentajeEnganche: String(percentage || 0) });
    },
    [patchQuote, quote.porcentajeEnganche, quote.precioVehiculo],
  );

  const handleDownPaymentPercentage = useCallback(
    (value) => {
      const percentage = Math.max(0, Math.min(80, toNumber(value)));
      const price = toNumber(quote.precioVehiculo);
      patchQuote({
        porcentajeEnganche: String(percentage),
        enganche: price > 0 ? String(Math.round((price * percentage) / 100)) : quote.enganche,
      });
    },
    [patchQuote, quote.enganche, quote.precioVehiculo],
  );

  return (
    <div className="container-fluid nr-shell py-3 px-2 px-md-3">
      <style>{`
        :root {
          --nr-primary: #0b3b75;
          --nr-primary-2: #0e5aa7;
          --nr-accent: #2d7ff9;
          --nr-cyan: #1db7c9;
          --nr-success: #18a66a;
          --nr-warning: #d98518;
          --nr-danger: #d83b4b;
          --nr-ink: #172033;
          --nr-muted: #667085;
          --nr-line: #e4e9f1;
          --nr-bg: #f4f7fb;
        }

        .nr-shell {
          min-height: 100vh;
          background:
            radial-gradient(circle at 8% 0%, rgba(45, 127, 249, .08), transparent 26rem),
            radial-gradient(circle at 95% 18%, rgba(29, 183, 201, .07), transparent 24rem),
            var(--nr-bg);
        }
        .nr-flow-page { color: var(--nr-ink); max-width: 1540px; margin: 0 auto; }
        .nr-hero {
          position: relative;
          overflow: hidden;
          border-radius: 24px;
          padding: 24px;
          color: #fff;
          background: linear-gradient(120deg, #082f62 0%, #0d4f91 55%, #1375b5 100%);
          box-shadow: 0 18px 45px rgba(8, 47, 98, .18);
        }
        .nr-hero::after {
          content: "";
          position: absolute;
          width: 320px;
          height: 320px;
          right: -100px;
          top: -170px;
          border-radius: 50%;
          border: 55px solid rgba(255,255,255,.08);
        }
        .nr-hero-content { position: relative; z-index: 1; }
        .nr-hero-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          border: 1px solid rgba(255,255,255,.22);
          border-radius: 999px;
          background: rgba(255,255,255,.11);
          font-size: 12px;
          font-weight: 800;
          letter-spacing: .04em;
          text-transform: uppercase;
        }
        .nr-hero h2 { font-size: clamp(1.35rem, 2vw, 2rem); font-weight: 850; letter-spacing: -.035em; }
        .nr-hero-copy { max-width: 780px; color: rgba(255,255,255,.78); font-size: .94rem; }
        .nr-progress-panel {
          min-width: 260px;
          max-width: 340px;
          padding: 14px 16px;
          border: 1px solid rgba(255,255,255,.18);
          border-radius: 16px;
          background: rgba(255,255,255,.1);
          backdrop-filter: blur(8px);
        }
        .nr-progress-track { height: 8px; overflow: hidden; border-radius: 99px; background: rgba(255,255,255,.18); }
        .nr-progress-value { height: 100%; border-radius: inherit; background: linear-gradient(90deg, #5be0d5, #fff); transition: width .25s ease; }
        .nr-progress-copy { color: rgba(255,255,255,.75); font-size: 12px; }
        .nr-hero .btn { min-height: 42px; border-radius: 11px; font-weight: 750; }
        .nr-hero .btn-light { color: var(--nr-primary); }
        .nr-hero .btn-outline-light { border-color: rgba(255,255,255,.55); }

        .nr-error-alert {
          border: 1px solid #f6d7a8;
          border-left: 5px solid var(--nr-warning);
          border-radius: 14px;
          background: #fff9ef;
          color: #74460c;
          box-shadow: 0 8px 24px rgba(116, 70, 12, .06);
        }

        .nr-mobile-tabs {
          position: sticky;
          top: 76px;
          z-index: 15;
          padding: 5px;
          border: 1px solid var(--nr-line);
          border-radius: 14px;
          background: rgba(255,255,255,.95);
          box-shadow: 0 8px 20px rgba(23, 32, 51, .08);
          backdrop-filter: blur(10px);
        }
        .nr-mobile-tabs .btn { border: 0; border-radius: 10px !important; font-weight: 750; }

        .nr-stepper {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 8px;
          padding: 10px;
          border: 1px solid var(--nr-line);
          border-radius: 18px;
          background: rgba(255,255,255,.92);
          box-shadow: 0 8px 24px rgba(23, 32, 51, .04);
        }
        .nr-step-button {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          padding: 9px;
          border: 0;
          border-radius: 12px;
          background: transparent;
          color: var(--nr-muted);
          text-align: left;
          transition: .2s ease;
        }
        .nr-step-button:hover { background: #f1f6fc; color: var(--nr-primary); transform: translateY(-1px); }
        .nr-step-number {
          display: grid;
          place-items: center;
          flex: 0 0 27px;
          height: 27px;
          border-radius: 9px;
          background: #edf1f6;
          color: #667085;
          font-size: 12px;
          font-weight: 850;
        }
        .nr-step-button.is-done .nr-step-number { background: #ddf7ec; color: var(--nr-success); }
        .nr-step-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; font-weight: 800; }

        .nr-card {
          border: 1px solid rgba(15, 23, 42, .09);
          border-radius: 20px;
          background: rgba(255,255,255,.97);
          box-shadow: 0 10px 30px rgba(15, 23, 42, .055);
        }
        .nr-section-card { scroll-margin-top: 94px; overflow: hidden; margin-bottom: 16px; transition: border-color .2s ease, box-shadow .2s ease; }
        .nr-section-card:hover { border-color: rgba(45,127,249,.22); box-shadow: 0 14px 34px rgba(15, 23, 42, .075); }
        .nr-section-complete { border-color: rgba(24,166,106,.2); }
        .nr-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 18px 20px;
          border-bottom: 1px solid #edf0f5;
          background: linear-gradient(180deg, #fff, #fcfdff);
        }
        .nr-section-heading { display: flex; align-items: flex-start; gap: 13px; min-width: 0; }
        .nr-section-icon {
          display: grid;
          place-items: center;
          flex: 0 0 42px;
          height: 42px;
          border-radius: 13px;
          background: linear-gradient(145deg, #e9f3ff, #f3f9ff);
          color: var(--nr-primary-2);
          box-shadow: inset 0 0 0 1px rgba(45,127,249,.1);
        }
        .nr-section-kicker { color: var(--nr-accent); font-size: 10px; font-weight: 850; letter-spacing: .09em; text-transform: uppercase; }
        .nr-section-title { margin: 1px 0 0; color: var(--nr-primary); font-size: 1.02rem; font-weight: 850; letter-spacing: -.02em; }
        .nr-section-subtitle { margin: 3px 0 0; color: var(--nr-muted); font-size: 12px; }
        .nr-section-body { padding: 20px; }
        .nr-status { display: inline-flex; align-items: center; gap: 5px; padding: 6px 9px; border-radius: 999px; font-size: 10px; font-weight: 850; letter-spacing: .03em; text-transform: uppercase; }
        .nr-status-success { background: #e5f8ef; color: #087c4a; }
        .nr-status-pending { background: #f4f5f7; color: #667085; }
        .nr-status-loading { background: #eaf2ff; color: #2a67b6; }

        .nr-field { margin-bottom: 17px; }
        .nr-field-label { display: flex; align-items: center; gap: 7px; margin-bottom: 7px; color: #344054; font-size: 12px; font-weight: 780; }
        .nr-required, .nr-optional { padding: 2px 6px; border-radius: 999px; font-size: 8px; font-weight: 850; letter-spacing: .04em; text-transform: uppercase; }
        .nr-required { background: #fff0f1; color: #c62f41; }
        .nr-optional { background: #f2f4f7; color: #7c8594; }
        .nr-control {
          min-height: 43px;
          border: 1px solid #d9e0ea;
          border-radius: 11px;
          background-color: #fbfcfe;
          color: var(--nr-ink);
          font-size: 13px;
          box-shadow: none !important;
          transition: border-color .18s ease, background .18s ease, box-shadow .18s ease !important;
        }
        .nr-control:hover:not(:disabled):not([readonly]) { border-color: #b7c5d8; background-color: #fff; }
        .nr-control:focus { border-color: var(--nr-accent); background-color: #fff; box-shadow: 0 0 0 4px rgba(45,127,249,.11) !important; }
        .nr-control-filled { background-color: #fff; }
        .nr-control:disabled, .nr-control[readonly] { cursor: not-allowed; background-color: #f1f3f6; color: #7b8493; opacity: 1; }
        .nr-control-wrap { position: relative; }
        .nr-control-loader { position: absolute; right: 36px; top: 13px; color: var(--nr-accent); pointer-events: none; }
        .nr-field-help { display: flex; align-items: flex-start; gap: 5px; margin-top: 6px; color: #7b8493; font-size: 10px; line-height: 1.35; }
        .nr-field-help svg { flex: 0 0 auto; margin-top: 0; }
        .nr-money-control { position: relative; }
        .nr-money-control .nr-control { padding-left: 30px; padding-right: 50px; }
        .nr-money-prefix, .nr-money-suffix { position: absolute; z-index: 2; top: 50%; transform: translateY(-50%); pointer-events: none; font-size: 11px; font-weight: 800; color: #7b8493; }
        .nr-money-prefix { left: 12px; }
        .nr-money-suffix { right: 11px; font-size: 9px; }

        .nr-subsection {
          padding: 16px;
          border: 1px solid #e8edf4;
          border-radius: 15px;
          background: #fafcff;
        }
        .nr-subsection-title { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; color: #44516a; font-size: 12px; font-weight: 850; text-transform: uppercase; letter-spacing: .05em; }
        .nr-range-panel { padding: 14px 16px; border: 1px solid #e3eaf4; border-radius: 15px; background: linear-gradient(135deg, #f5f9ff, #fbfdff); }
        .nr-range-value { color: var(--nr-primary); font-size: 1rem; font-weight: 850; }
        .nr-range { width: 100%; accent-color: var(--nr-accent); }
        .nr-range-scale { display: flex; justify-content: space-between; color: #98a2b3; font-size: 9px; }

        .nr-action-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 14px;
          margin-bottom: 16px;
          border: 1px solid #dce7f5;
          border-radius: 16px;
          background: linear-gradient(100deg, #f5f9ff, #fbfdff);
        }
        .nr-ready-dot { display: inline-block; width: 8px; height: 8px; margin-right: 6px; border-radius: 50%; background: var(--nr-success); box-shadow: 0 0 0 4px rgba(24,166,106,.12); }
        .nr-not-ready-dot { background: var(--nr-warning); box-shadow: 0 0 0 4px rgba(217,133,24,.12); }
        .nr-primary-action {
          min-height: 46px;
          padding-inline: 20px;
          border: 0;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--nr-primary), var(--nr-primary-2));
          box-shadow: 0 9px 20px rgba(11,59,117,.18);
          font-weight: 800;
        }
        .nr-primary-action:hover { transform: translateY(-1px); box-shadow: 0 12px 24px rgba(11,59,117,.24); }
        .nr-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 42px; border-radius: 11px; font-weight: 750; font-size: 12px; }

        .nr-metric { display: flex; align-items: flex-start; gap: 11px; min-height: 93px; padding: 15px; border: 1px solid #e3e8ef; border-radius: 15px; background: #fff; }
        .nr-metric-icon { display: grid; place-items: center; flex: 0 0 34px; height: 34px; border-radius: 10px; background: #edf5ff; color: var(--nr-accent); }
        .nr-metric-label { margin-bottom: 5px; color: #7b8493; font-size: 9px; font-weight: 850; letter-spacing: .055em; text-transform: uppercase; }
        .nr-metric-value { color: var(--nr-ink); font-size: 1rem; font-weight: 850; line-height: 1.2; overflow-wrap: anywhere; }
        .nr-metric-muted .nr-metric-value { color: #98a2b3; }
        .nr-metric-highlight { border-color: rgba(45,127,249,.28); background: linear-gradient(145deg, #eef6ff, #fff); }
        .nr-json { max-height: 360px; overflow: auto; margin: 0; padding: 14px; border-radius: 13px; background: #0b1220; color: #d8e7ff; font-size: 11px; }

        .nr-preview {
          position: relative;
          overflow: hidden;
          padding: 18px;
          color: #fff;
          background: linear-gradient(155deg, #082f62 0%, #0c4f91 55%, #0b6ca4 100%);
          box-shadow: 0 18px 45px rgba(8,47,98,.2);
        }
        .nr-preview::before {
          content: "";
          position: absolute;
          width: 220px;
          height: 220px;
          right: -90px;
          top: -95px;
          border-radius: 50%;
          background: rgba(255,255,255,.07);
        }
        .nr-preview-content { position: relative; z-index: 1; }
        .nr-sticky { position: sticky; top: 92px; z-index: 4; }
        .nr-preview-kicker { color: rgba(255,255,255,.7); font-size: 10px; font-weight: 850; letter-spacing: .08em; text-transform: uppercase; }
        .nr-preview-title { margin: 2px 0 0; font-size: 1.25rem; font-weight: 850; letter-spacing: -.025em; }
        .nr-preview-badge { padding: 6px 9px; border: 1px solid rgba(255,255,255,.22); border-radius: 999px; background: rgba(255,255,255,.12); color: #fff; font-size: 9px; font-weight: 850; letter-spacing: .05em; text-transform: uppercase; }
        .nr-preview-card { padding: 14px; border: 1px solid rgba(255,255,255,.13); border-radius: 15px; background: rgba(255,255,255,.98); color: var(--nr-ink); box-shadow: 0 8px 20px rgba(0,0,0,.08); }
        .nr-preview-label { color: #7b8493; font-size: 9px; font-weight: 850; letter-spacing: .055em; text-transform: uppercase; }
        .nr-preview-main { color: var(--nr-primary); font-size: 1rem; font-weight: 850; line-height: 1.3; }
        .nr-preview-muted { color: #7b8493; font-size: 10px; }
        .nr-monthly-card { padding: 17px; border-radius: 17px; background: linear-gradient(135deg, #fff, #edf6ff); color: var(--nr-ink); box-shadow: 0 10px 24px rgba(0,0,0,.09); }
        .nr-monthly-value { color: var(--nr-primary-2); font-size: clamp(1.55rem, 2vw, 2rem); font-weight: 900; letter-spacing: -.04em; line-height: 1.05; }
        .nr-preview-progress { height: 7px; overflow: hidden; border-radius: 99px; background: rgba(255,255,255,.2); }
        .nr-preview-progress > span { display: block; height: 100%; border-radius: inherit; background: #72e4d7; }
        .nr-next-card { padding: 13px; border: 1px solid rgba(255,255,255,.16); border-radius: 14px; background: rgba(255,255,255,.1); }
        .nr-preview .btn-light { color: var(--nr-primary); }
        .nr-preview .btn-outline-light:disabled { color: rgba(255,255,255,.55); border-color: rgba(255,255,255,.25); }

        .nr-spin { animation: nr-spin .9s linear infinite; }
        @keyframes nr-spin { to { transform: rotate(360deg); } }

        .nr-pdf-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          background: rgba(15, 23, 42, .78);
          backdrop-filter: blur(4px);
        }
        .nr-pdf-modal {
          display: flex;
          flex-direction: column;
          width: min(1180px, 100%);
          height: min(92vh, 900px);
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.18);
          border-radius: 18px;
          background: #fff;
          box-shadow: 0 24px 80px rgba(0,0,0,.38);
        }
        .nr-pdf-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 14px;
          border-bottom: 1px solid var(--nr-line);
          background: #fff;
        }
        .nr-pdf-modal-title {
          margin: 0;
          color: var(--nr-ink);
          font-size: .95rem;
          font-weight: 850;
        }
        .nr-pdf-modal-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .nr-pdf-frame {
          flex: 1;
          width: 100%;
          min-height: 0;
          border: 0;
          background: #eef2f7;
        }

        @media (max-width: 575.98px) {
          .nr-pdf-modal-backdrop { padding: 0; }
          .nr-pdf-modal {
            width: 100%;
            height: 100vh;
            border: 0;
            border-radius: 0;
          }
          .nr-pdf-modal-header { align-items: flex-start; }
        }

        @media (max-width: 1199.98px) {
          .nr-step-label { display: none; }
          .nr-step-button { justify-content: center; }
          .nr-stepper { grid-template-columns: repeat(6, 1fr); }
        }
        @media (max-width: 991.98px) {
          .nr-shell { padding-bottom: 30px !important; }
          .nr-hero { padding: 19px; border-radius: 19px; }
          .nr-progress-panel { width: 100%; max-width: none; min-width: 0; }
          .nr-stepper { position: sticky; top: 132px; z-index: 12; }
          .nr-sticky { position: static; }
        }
        @media (max-width: 575.98px) {
          .nr-section-header { align-items: flex-start; padding: 15px; }
          .nr-section-body { padding: 15px; }
          .nr-section-icon { flex-basis: 38px; height: 38px; }
          .nr-status { display: none; }
          .nr-action-bar { align-items: stretch; flex-direction: column; }
          .nr-action-bar .nr-primary-action { width: 100%; }
          .nr-stepper { gap: 4px; padding: 7px; }
          .nr-step-button { padding: 6px; }
          .nr-step-number { flex-basis: 25px; height: 25px; }
        }
      `}</style>

      {pdfPreviewOpen && pdfObjectUrl && (
        <div
          className="nr-pdf-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa de la cotización NR"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setPdfPreviewOpen(false);
            }
          }}
        >
          <div className="nr-pdf-modal">
            <div className="nr-pdf-modal-header">
              <div>
                <div className="nr-preview-kicker text-primary">Documento NR</div>
                <h6 className="nr-pdf-modal-title">
                  Cotización {idCotizacion ? `#${idCotizacion}` : ""}
                </h6>
              </div>

              <div className="nr-pdf-modal-actions">
                <a
                  href={pdfObjectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary nr-btn"
                >
                  <Icon name="file" size={15} /> Abrir en pestaña
                </a>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success nr-btn"
                  onClick={handleDownloadPdf}
                >
                  <Icon name="download" size={15} /> Descargar
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary nr-btn"
                  onClick={() => setPdfPreviewOpen(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>

            <iframe
              src={`${pdfObjectUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              title={`Cotización NR ${idCotizacion || ""}`}
              className="nr-pdf-frame"
            />
          </div>
        </div>
      )}

      <div className="nr-flow-page">
        <section className="nr-hero mb-3">
          <div className="nr-hero-content">
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
              <div>
                <div className="nr-hero-kicker mb-3"><Icon name="sparkles" size={15} /> Procotiza NR · Flujo unificado</div>
                <h2 className="mb-2">Financiamiento</h2>
                <p className="nr-hero-copy mb-0">
                  Captura la información en orden, consulta los catálogos NR y revisa en tiempo real cómo verá el cliente su propuesta.
                </p>
              </div>

              <div className="nr-progress-panel">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="small fw-bold">Avance de la propuesta</span>
                  <span className="fw-bold">{progressPercentage}%</span>
                </div>
                <div className="nr-progress-track mb-2">
                  <div className="nr-progress-value" style={{ width: `${progressPercentage}%` }} />
                </div>
                <div className="nr-progress-copy">
                  {completedSteps} de {flowSteps.length} pasos completos · {missingRequiredFields.length ? `${missingRequiredFields.length} campos requeridos pendientes` : "Lista para cotizar"}
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap mt-4">
              <button
                type="button"
                className="btn btn-light nr-btn"
                onClick={handleReloadCatalogs}
                disabled={loading.base || loading.anios}
              >
                <Icon
                  name={loading.base || loading.anios ? "loader" : "refresh"}
                  size={17}
                  className={loading.base || loading.anios ? "nr-spin" : ""}
                />
                {loading.base || loading.anios
                  ? "Actualizando catálogos"
                  : "Recargar catálogos"}
              </button>
              <button type="button" className="btn btn-outline-light nr-btn" onClick={handleReset}>
                <Icon name="trash" size={17} /> Limpiar propuesta
              </button>
            </div>
          </div>
        </section>

        {lastError && (
          <div className="alert nr-error-alert d-flex align-items-start justify-content-between gap-3 mb-3" role="alert">
            <div className="d-flex align-items-start gap-2">
              <Icon name="alert" size={19} />
              <div>
                <div className="fw-bold mb-1">No se pudo completar una consulta</div>
                <div className="small">{lastError}</div>
              </div>
            </div>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setLastError("")} />
          </div>
        )}

        <div className="nr-mobile-tabs d-lg-none mb-3">
          <div className="btn-group w-100">
            <button
              type="button"
              className={`btn ${activeTab === "asesor" ? "btn-primary" : "btn-light"}`}
              onClick={() => setActiveTab("asesor")}
            >
              Captura asesor
            </button>
            <button
              type="button"
              className={`btn ${activeTab === "cliente" ? "btn-primary" : "btn-light"}`}
              onClick={() => setActiveTab("cliente")}
            >
              Vista cliente
            </button>
          </div>
        </div>

        <div className="row g-3 g-xl-4 align-items-start">
          <div className={`col-xl-8 col-lg-7 ${activeTab !== "asesor" ? "d-none d-lg-block" : ""}`}>
            <nav className="nr-stepper mb-3" aria-label="Pasos de la propuesta">
              {flowSteps.map((step, index) => (
                <button
                  type="button"
                  key={step.id}
                  className={`nr-step-button ${step.done ? "is-done" : ""}`}
                  onClick={() => scrollToSection(step.id)}
                  title={`Ir a ${step.label}`}
                >
                  <span className="nr-step-number">{step.done ? <Icon name="check" size={15} /> : index + 1}</span>
                  <span className="nr-step-label">{step.shortLabel}</span>
                </button>
              ))}
            </nav>

            <SectionCard
              id="nr-step-client"
              step="1"
              title="Datos del cliente"
              subtitle="Opcional. Solo se usa en esta vista y no se envía ni se guarda en NR."
              icon="user"
              done={flowSteps[0].done}
            >
              <div className="row">
                <div className="col-md-6">
                  <TextInput
                    label="Nombre completo"
                    value={client.nombre}
                    onChange={(value) => patchClient({ nombre: value })}
                    placeholder="Ej. María López Hernández"
                    optional
                  />
                </div>
                <div className="col-md-6">
                  <TextInput
                    label="Teléfono"
                    value={client.telefono}
                    onChange={(value) => patchClient({ telefono: value.replace(/[^0-9+\s-]/g, "") })}
                    placeholder="222 000 0000"
                    optional
                  />
                </div>
                <div className="col-md-6">
                  <TextInput
                    label="Correo electrónico"
                    type="email"
                    value={client.correo_electronico}
                    onChange={(value) => patchClient({ correo_electronico: value })}
                    placeholder="cliente@correo.com"
                    optional
                  />
                </div>
                <div className="col-md-6">
                  <TextInput
                    label="Asesor"
                    value={client.asesor}
                    onChange={(value) => patchClient({ asesor: value })}
                    placeholder="Nombre del asesor"
                    help="Se precarga desde la sesión actual y puede editarse."
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              id="nr-step-fiscal"
              step="2"
              title="Operación y datos fiscales"
              subtitle="Define la operación que utilizará Procotiza para consultar los catálogos."
              icon="building"
              done={flowSteps[1].done}
            >
              <div className="row">
                <div className="col-md-4">
                  <SelectField
                    label="Forma de compra Babilonia"
                    value={quote.depende_auto}
                    onChange={(event) => patchQuote({ depende_auto: event.target.value })}
                    help="Conserva la equivalencia actual de depende_auto."
                  >
                    <option value="Si">Contado</option>
                    <option value="No">Crédito</option>
                  </SelectField>
                </div>
                <div className="col-md-4">
                  <SelectField
                    label="Personalidad jurídica"
                    value={quote.personalidadJuridica}
                    onChange={(event) => {
                      const selected = PERSONALIDADES.find((item) => item.id === event.target.value) || PERSONALIDADES[0];
                      patchQuote({
                        personalidadJuridica: selected.id,
                        idPerJuridica: selected.cotizaId,
                        fiscalPersonalityId: selected.fiscalId,
                        // Se limpia y se vuelve a pedir a NR: el catálogo de
                        // seguros de vida depende de personalidadJuridica
                        // (perJuridId) y puede traer opciones distintas.
                        seguroVidaId: "",
                      });
                    }}
                    required
                  >
                    {PERSONALIDADES.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                  </SelectField>
                </div>
                <div className="col-md-4">
                  <SelectField
                    label="Tipo de operación NR"
                    value={quote.tipoOperacionId}
                    onChange={(event) => patchQuote({ tipoOperacionId: event.target.value, planId: "", planNombre: "", plazo: "" })}
                    required
                  >
                    {TIPOS_OPERACION.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                  </SelectField>
                </div>
                <div className="col-md-4">
                  <TextInput
                    label="Agencia NR"
                    value={quote.agenciaClave}
                    onChange={(value) => patchQuote({ agenciaClave: value })}
                    help="La clave validada para guardar cotizaciones en UAT es NRFM. Puede cambiarse desde el archivo .env."
                    readOnly
                    required
                  />
                </div>
                <div className="col-md-4">
                  <TextInput
                    label="AgenciaId NR"
                    value={quote.agenciaId}
                    onChange={(value) => patchQuote({ agenciaId: value.replace(/[^0-9]/g, ""), marcaId: "", marcaNombre: "" })}
                    help="Actualmente se utiliza 101 para consultar catálogos."
                    required
                  />
                </div>
                <div className="col-md-4">
                  <CatalogSelect
                    label="Clasificación"
                    value={quote.clasificacionId}
                    options={catalogs.clasificaciones}
                    required
                    onChange={(value, item) => patchQuote({
                      clasificacionId: value,
                      clasificacionNombre: nrGetName(item) || "",
                      marcaId: "",
                      marcaNombre: "",
                      modeloId: "",
                      modeloNombre: "",
                      versionId: "",
                      versionNombre: "",
                      productoId: "",
                      productoNombre: "",
                      planId: "",
                      planNombre: "",
                    })}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              id="nr-step-vehicle"
              step="3"
              title="Selección del vehículo"
              subtitle="Los campos se habilitan en cascada para evitar combinaciones inválidas."
              icon="car"
              done={flowSteps[2].done}
              loading={Boolean(catalogLoading)}
            >
              <div className="row">
                <div className="col-md-6">
                  <CatalogSelect
                    label="Marca NR"
                    value={quote.marcaId}
                    options={catalogs.marcas}
                    disabled={!quote.clasificacionId}
                    loading={loading.marcas}
                    required
                    onChange={(value, item) => patchQuote({
                      marcaId: value,
                      marcaNombre: nrGetName(item),
                      modeloId: "",
                      modeloNombre: "",
                      versionId: "",
                      versionNombre: "",
                      productoId: "",
                      productoNombre: "",
                      planId: "",
                      planNombre: "",
                    })}
                  />
                </div>
                <div className="col-md-6">
                  <CatalogSelect
                    label="Modelo NR"
                    value={quote.modeloId}
                    options={catalogs.modelos}
                    disabled={!quote.marcaId}
                    loading={loading.modelos}
                    required
                    onChange={(value, item) => patchQuote({
                      modeloId: value,
                      modeloNombre: nrGetName(item),
                      versionId: "",
                      versionNombre: "",
                      productoId: "",
                      productoNombre: "",
                      planId: "",
                      planNombre: "",
                    })}
                  />
                </div>
                <div className="col-md-4">
                  <CatalogSelect
                    label="Año"
                    value={quote.anio}
                    options={catalogs.anios}
                    loading={loading.anios}
                    required
                    placeholder="Seleccione un año NR"
                    getValue={(item) =>
                      safeString(
                        nrGetValue(item, [
                          "anio",
                          "Anio",
                          "año",
                          "Año",
                          "anioModelo",
                          "AnioModelo",
                          "idAnio",
                          "IdAnio",
                        ]) || nrGetId(item),
                      )
                    }
                    getLabel={(item) =>
                      safeString(
                        nrGetValue(item, [
                          "anio",
                          "Anio",
                          "año",
                          "Año",
                          "anioModelo",
                          "AnioModelo",
                          "idAnio",
                          "IdAnio",
                        ]) ||
                          nrGetName(item) ||
                          nrGetId(item),
                      )
                    }
                    help="Los años se consultan directamente a NR sin enviar filtros."
                    onChange={(value) =>
                      patchQuote({
                        anio: value,
                        versionId: "",
                        versionNombre: "",
                        productoId: "",
                        productoNombre: "",
                        planId: "",
                        planNombre: "",
                      })
                    }
                  />
                </div>
                <div className="col-md-8">
                  <CatalogSelect
                    label="Versión NR"
                    value={quote.versionId}
                    options={catalogs.versiones}
                    disabled={!quote.anio || !quote.modeloId}
                    loading={loading.versiones}
                    required
                    onChange={(value, item) => patchQuote({
                      versionId: value,
                      versionNombre: nrGetName(item),
                      productoId: "",
                      productoNombre: "",
                      planId: "",
                      planNombre: "",
                    })}
                  />
                </div>
                <div className="col-md-6">
                  <CatalogSelect
                    label="Estrategia NR"
                    value={quote.estrategiaId}
                    options={catalogs.estrategias}
                    disabled={!quote.versionId}
                    loading={loading.productos}
                    onChange={(value, item) => patchQuote({
                      estrategiaId: value,
                      estrategiaNombre: nrGetName(item),
                      productoId: "",
                      productoNombre: "",
                      planId: "",
                      planNombre: "",
                    })}
                  />
                </div>
                <div className="col-md-6">
                  <CatalogSelect
                    label="Producto NR"
                    value={quote.productoId}
                    options={catalogs.productos}
                    disabled={!quote.estrategiaId}
                    loading={loading.productos}
                    required
                    help="Se usa exclusivamente el catálogo oficial de NR. Si NR no regresa productos para esta combinación, no se muestra ninguna opción."
                    onChange={(value, item) => {
                      const filteredProduct =
                        findProductInFiltradoByVersion(
                          catalogs.productosFiltrados,
                          item,
                        ) || item;

                      patchQuote({
                        productoId: value,
                        productoNombre: nrGetName(item),
                        productoTipoPrecioId: nrGetValue(filteredProduct, [
                          "idTipoPrecio",
                          "IdTipoPrecio",
                          "tipoDePrecio",
                          "TipoDePrecio",
                          "tipoCxa",
                          "TipoCxa",
                        ]),
                        // Igual que en la selección automática: sin
                        // fallback al id interno si NR no trae idExterno.
                        anzenProductoId: getProductExternalId(filteredProduct),
                        idTipoProducto: safeString(
                          getProductTypeId(filteredProduct),
                        ),
                        idBroker:
                          getProductBrokerId(filteredProduct) || "0",
                        precioVehiculo:
                          getProductPrice(
                            filteredProduct,
                            quote.clasificacionId,
                          ) ||
                          getProductPrice(item, quote.clasificacionId),
                        planId: "",
                        planNombre: "",
                        paqueteExternoId: "",
                        seguroAnzenKey: "",
                        montoSeguro: "0",
                      });
                    }}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              id="nr-step-plan"
              step="4"
              title="Plan, plazo y seguro"
              subtitle="Selecciona el paquete de Procotiza y completa la configuración del seguro."
              icon="shield"
              done={flowSteps[3].done}
              loading={Boolean(planLoading)}
            >
              <div className="row">
                <div className="col-md-6">
                  <CatalogSelect
                    label="Plan / paquete NR"
                    value={quote.planId}
                    options={catalogs.planes.length ? catalogs.planes : catalogs.paquetes}
                    disabled={!quote.productoId}
                    loading={loading.planes}
                    required
                    onChange={(value, item) => {
                      const packageMatch =
                        findById(
                          catalogs.paquetes,
                          value,
                          ["idPaquete", "IdPaquete"],
                        ) || item;

                      patchQuote({
                        planId: value,
                        planNombre: nrGetName(item),
                        paqueteExternoId: resolvePackageExternalId(
                          "",
                          packageMatch,
                          value,
                        ),
                        plazo: "",
                        tipoSeguroId: quote.tipoSeguroId || "32",
                        seguroAnzenKey: "",
                        montoSeguro: "0",
                      });
                    }}
                    help="Corresponde al IdPaquete requerido por Procotiza; no se compara contra Gold, Elite o Platinum."
                  />
                </div>
                <div className="col-md-6">
                  <CatalogSelect
                    label="Plazo NR"
                    value={quote.plazo}
                    options={catalogs.plazos}
                    disabled={!quote.planId}
                    loading={loading.plan}
                    required
                    getValue={getPlazoValue}
                    getLabel={(item) => `${getPlazoValue(item)} meses`}
                    onChange={(value) => patchQuote({ plazo: value, seguroAnzenKey: "", montoSeguro: "0" })}
                  />
                </div>
                <div className="col-md-6">
                  <CatalogSelect
                    label="Estado"
                    value={quote.estadoId}
                    options={catalogs.estados}
                    required
                    onChange={(value, item) => patchQuote({
                      estadoId: value,
                      estadoNombre: nrGetName(item),
                      municipioId: "",
                      municipioNombre: "",
                      aseguradoraId: "",
                      aseguradoraNombre: "",
                      coberturaId: "",
                      coberturaNombre: "",
                      seguroAnzenKey: "",
                      montoSeguro: "0",
                    })}
                  />
                </div>
                <div className="col-md-6">
                  <CatalogSelect
                    label="Municipio"
                    value={quote.municipioId}
                    options={municipiosOptions}
                    disabled={!quote.estadoId}
                    loading={loading.anzen}
                    optional
                    onChange={(value, item) => patchQuote({ municipioId: value, municipioNombre: nrGetName(item), seguroAnzenKey: "", montoSeguro: "0" })}
                  />
                </div>
              </div>

              <div className="nr-subsection mt-1">
                <div className="nr-subsection-title"><Icon name="shield" size={16} /> Configuración de seguro</div>
                <div className="row">
                  <div className="col-md-4">
                    <CatalogSelect
                      label="Tipo de seguro"
                      value={quote.tipoSeguroId}
                      options={catalogs.tiposSeguro}
                      disabled={!quote.planId}
                      loading={loading.plan}
                      onChange={(value, item) => patchQuote({ tipoSeguroId: value, tipoSeguroNombre: nrGetName(item), seguroAnzenKey: "", montoSeguro: "0" })}
                    />
                  </div>
                  <div className="col-md-4">
                    <CatalogSelect
                      label="Seguro de vida"
                      value={quote.seguroVidaId}
                      options={catalogs.segurosVida}
                      disabled={!quote.planId}
                      loading={loading.plan}
                      onChange={(value) => patchQuote({ seguroVidaId: value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <CatalogSelect
                      label="Aseguradora"
                      value={quote.aseguradoraId}
                      options={aseguradorasOptions}
                      disabled={!quote.estadoId}
                      loading={loading.anzen}
                      getValue={(item) => nrGetValue(item, ["insuranceId", "InsuranceId", "id", "Id"])}
                      onChange={(value, item) => patchQuote({ aseguradoraId: value, aseguradoraNombre: nrGetName(item), seguroAnzenKey: "", montoSeguro: "0" })}
                    />
                  </div>
                  <div className="col-md-4">
                    <CatalogSelect
                      label="Cobertura"
                      value={quote.coberturaId}
                      options={coberturasOptions}
                      disabled={!quote.estadoId}
                      loading={loading.anzen}
                      getValue={(item) => nrGetValue(item, ["typeOfCoverageId", "TypeOfCoverageId", "id", "Id"])}
                      onChange={(value, item) => patchQuote({ coberturaId: value, coberturaNombre: nrGetName(item), seguroAnzenKey: "", montoSeguro: "0" })}
                    />
                  </div>
                  <div className="col-md-4">
                    <CatalogSelect
                      label="Uso (catálogo Anzen)"
                      value={quote.tipoUsoId}
                      options={usosOptions}
                      disabled={!quote.estadoId}
                      loading={loading.anzen}
                      getValue={(item) => nrGetValue(item, ["typeOfUsesId", "TypeOfUsesId", "typeOfUseId", "TypeOfUseId", "id", "Id"])}
                      onChange={(value, item) => patchQuote({ tipoUsoId: value, tipoUsoNombre: nrGetName(item), seguroAnzenKey: "", montoSeguro: "0" })}
                    />
                  </div>
                  <div className="col-md-4">
                    <CatalogSelect
                      label="UDI"
                      value={quote.udiId}
                      options={udiOptions}
                      disabled={!quote.estadoId}
                      loading={loading.anzen}
                      getValue={(item) => nrGetValue(item, ["udiLevelId", "UdiLevelId", "id", "Id"])}
                      onChange={(value, item) => patchQuote({ udiId: value, udiNombre: nrGetName(item), seguroAnzenKey: "", montoSeguro: "0" })}
                    />
                  </div>
                  <div className="col-12">
                    <div className="nr-action-bar mt-2">
                      <div>
                        <div className="fw-bold small">
                          Cotización automática de seguro
                        </div>
                        <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                          {anzenError
                            ? anzenError.code
                              ? `${anzenError.message} (${anzenError.code})`
                              : anzenError.message
                            : canQueryAnzen
                              ? anzenInsuranceOptions.length
                                ? `${anzenInsuranceOptions.length} opción(es) disponibles en Anzen.`
                                : "Los datos están completos; la consulta se ejecutará automáticamente."
                              : `Pendiente: ${missingAnzenFields.join(", ") || "completar configuración"}.`}
                        </div>
                        {!canQueryAnzen &&
                          missingAnzenFields.some((label) =>
                            label.startsWith("Tipo de producto"),
                          ) && (
                            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                              El producto sí está seleccionado: NR no regresó
                              su "tipo de producto" en el catálogo de
                              productos para este vehículo. No es necesario
                              para cotizar/guardar/generar PDF — solo bloquea
                              la consulta automática a Anzen. Puedes seguir
                              capturando el seguro manualmente.
                            </div>
                          )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary nr-btn"
                        disabled={!canQueryAnzen || loading.segurosAnzen}
                        onClick={() =>
                          handleConsultarSegurosAnzen({ silent: false })
                        }
                      >
                        <Icon
                          name={loading.segurosAnzen ? "loader" : "refresh"}
                          size={16}
                          className={loading.segurosAnzen ? "nr-spin" : ""}
                        />
                        {loading.segurosAnzen
                          ? "Consultando"
                          : "Actualizar seguros"}
                      </button>
                    </div>
                  </div>
                  <div className="col-12">
                    <CatalogSelect
                      label="Cotización de seguro Anzen"
                      value={quote.seguroAnzenKey}
                      options={anzenInsuranceOptions}
                      disabled={!anzenInsuranceOptions.length}
                      loading={loading.segurosAnzen}
                      optional
                      placeholder="Seleccione una cotización"
                      getValue={(item) => safeString(item.__key)}
                      getLabel={(item) => safeString(item.__label)}
                      help="Al seleccionar una opción se llena automáticamente el monto del seguro."
                      onChange={(value, item) =>
                        patchQuote(buildInsurancePatch(item, anzenCotizacion))
                      }
                    />
                  </div>

                  {anzenError && (
                    <div className="col-12">
                      <div className="alert alert-warning mb-0 py-2 px-3 small">
                        <div className="fw-bold">
                          La cotización de seguro Anzen no está disponible.
                        </div>
                        <div className="mt-1">
                          {anzenError.message}
                          {anzenError.code ? ` Código: ${anzenError.code}.` : ""}
                        </div>
                        <div className="mt-1">
                          La propuesta puede cotizarse con monto de seguro en $0,
                          pero la póliza del vehículo no quedó validada por Anzen.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>

            <SectionCard
              id="nr-step-amounts"
              step="5"
              title="Montos de la cotización"
              subtitle="Captura los importes que Procotiza utilizará para calcular la mensualidad oficial."
              icon="wallet"
              done={flowSteps[4].done}
            >
              <div className="row">
                <div className="col-md-6">
                  <MoneyInput
                    label="Precio del vehículo"
                    value={quote.precioVehiculo}
                    onChange={(value) => patchQuote({ precioVehiculo: value })}
                    help="Se precarga con el precio del producto NR cuando el servicio lo regresa."
                    required
                  />
                </div>
                <div className="col-md-6">
                  <MoneyInput
                    label="Enganche"
                    value={quote.enganche}
                    onChange={handleDownPaymentChange}
                    help="Si queda vacío, se calcula con el porcentaje de enganche seleccionado."
                    optional
                  />
                </div>
              </div>

              <div className="nr-range-panel mb-4">
                <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                  <div>
                    <div className="small fw-bold text-dark">Porcentaje de enganche</div>
                    <div className="small text-muted">Mueve la barra para calcular el enganche automáticamente.</div>
                  </div>
                  <div className="nr-range-value">{toNumber(quote.porcentajeEnganche)}%</div>
                </div>
                <input
                  type="range"
                  className="nr-range"
                  min="0"
                  max="80"
                  step="1"
                  value={Math.min(80, Math.max(0, toNumber(quote.porcentajeEnganche)))}
                  onChange={(event) => handleDownPaymentPercentage(event.target.value)}
                  aria-label="Porcentaje de enganche"
                />
                <div className="nr-range-scale"><span>0%</span><span>20%</span><span>40%</span><span>60%</span><span>80%</span></div>
              </div>

              <div className="nr-subsection">
                <div className="nr-subsection-title"><Icon name="calculator" size={16} /> Adicionales, seguro y comisión</div>
                <div className="row">
                  <div className="col-md-3 col-sm-6">
                    <MoneyInput label="Accesorios financiados" value={quote.accesoriosFinanciados} onChange={(value) => patchQuote({ accesoriosFinanciados: value })} />
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <MoneyInput label="Accesorios de contado" value={quote.accesoriosContado} onChange={(value) => patchQuote({ accesoriosContado: value })} />
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <MoneyInput label="Garantía extendida" value={quote.garantiaExtendida} onChange={(value) => patchQuote({ garantiaExtendida: value })} />
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <MoneyInput label="Productos adicionales" value={quote.productosAdicionales} onChange={(value) => patchQuote({ productosAdicionales: value })} />
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <SelectField label="Compra del seguro" value={quote.tipoCompraSeguro} onChange={(event) => patchQuote({ tipoCompraSeguro: event.target.value })}>
                      <option value="Contado">Contado</option>
                      <option value="Financiado">Financiado</option>
                    </SelectField>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <MoneyInput
                      label="Monto del seguro"
                      value={quote.montoSeguro}
                      onChange={(value) => patchQuote({ montoSeguro: value })}
                      help={
                        quote.seguroAnzenKey
                          ? "Importe precargado con la cotización seleccionada de Anzen; puede ajustarse manualmente."
                          : "Se llenará automáticamente cuando Anzen regrese una opción válida."
                      }
                    />
                  </div>
                  <div className="col-md-6 col-sm-6">
                    <MoneyInput
                      label="Comisión de apertura NR"
                      value={nrCotizacion ? safeString(nrMontoCxa) : "0"}
                      onChange={() => {}}
                      readOnly
                      help={
                        nrCotizacion
                          ? `Procotiza calculó ${formatCurrency(nrMontoCxa)} de CxA${nrPagoCxa ? `, con pago mensual de ${formatCurrency(nrPagoCxa)}` : ""}.`
                          : "Se envía ComisionApertura en 0 y Procotiza calcula la CxA configurada en el paquete."
                      }
                    />
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              id="nr-step-result"
              step="6"
              title="Resultado Procotiza"
              subtitle="Cotiza, guarda la operación y genera el documento final sin salir de la pantalla."
              icon="chart"
              done={flowSteps[5].done}
            >
              <div className="nr-action-bar">
                <div>
                  <div className="fw-bold small">
                    <span className={`nr-ready-dot ${!isQuoteReady ? "nr-not-ready-dot" : ""}`} />
                    {isQuoteReady ? "La propuesta está lista para cotizar" : `Faltan ${missingRequiredFields.length} campos requeridos`}
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                    {isQuoteReady
                      ? "Procotiza recibirá los IDs seleccionados y los montos capturados."
                      : `Siguiente pendiente: ${missingRequiredFields[0]?.label || "Completar información"}.`}
                  </div>
                </div>
                <button type="button" className="btn btn-primary nr-primary-action nr-btn" onClick={handleCotizarNR} disabled={loading.cotiza}>
                  <Icon name={loading.cotiza ? "loader" : "calculator"} size={18} className={loading.cotiza ? "nr-spin" : ""} />
                  {loading.cotiza ? "Cotizando con NR" : "Cotizar con NR"}
                </button>
              </div>

              <div className="row g-2 g-md-3 mb-3">
                <div className="col-md-6 col-xl-3">
                  <ReadOnlyMetric label="Mensualidad total NR" value={nrMensualidad ? formatCurrency(nrMensualidad) : "Pendiente"} muted={!nrMensualidad} highlight={Boolean(nrMensualidad)} icon="chart" />
                </div>
                <div className="col-md-6 col-xl-3">
                  <ReadOnlyMetric label="Pago inicial NR" value={nrPagoInicial ? formatCurrency(nrPagoInicial) : "Pendiente"} muted={!nrPagoInicial} icon="wallet" />
                </div>
                <div className="col-md-6 col-xl-3">
                  <ReadOnlyMetric label="Monto a financiar NR" value={nrMontoFinanciar ? formatCurrency(nrMontoFinanciar) : "Pendiente"} muted={!nrMontoFinanciar} icon="save" />
                </div>
                <div className="col-md-6 col-xl-3">
                  <ReadOnlyMetric label="Tasa NR" value={nrCotizacion ? `${Number(nrTasa.toFixed(2))}%` : "Pendiente"} muted={!nrCotizacion} icon="calculator" />
                </div>
                <div className="col-md-6 col-xl-3">
                  <ReadOnlyMetric
                    label="Comisión de apertura NR"
                    value={nrCotizacion ? formatCurrency(nrMontoCxa) : "Pendiente"}
                    muted={!nrCotizacion}
                    icon="wallet"
                  />
                </div>
                <div className="col-md-6 col-xl-3">
                  <ReadOnlyMetric
                    label="Seguro anual NR"
                    value={nrCotizacion ? formatCurrency(nrSeguroAnual) : "Pendiente"}
                    muted={!nrCotizacion}
                    icon="shield"
                  />
                </div>
                <div className="col-md-6 col-xl-3">
                  <ReadOnlyMetric
                    label="Seguro de vida mensual"
                    value={nrCotizacion ? formatCurrency(nrPagoSeguroVida) : "Pendiente"}
                    muted={!nrCotizacion}
                    icon="shield"
                  />
                </div>
              </div>

              {hasDownPaymentMismatch && (
                <div className="alert alert-warning py-2 px-3 small mb-3">
                  <div className="fw-bold">
                    NR devolvió un enganche distinto al enviado.
                  </div>
                  <div className="mt-1">
                    Se enviaron {formatCurrency(requestedDownPayment)} y NR
                    regresó {formatCurrency(nrEngancheReportado)}. El monto a
                    financiar sí se muestra con el valor calculado por NR.
                  </div>
                  <div className="mt-1">
                    La cotización se solicita con IdCotizacion en 0 para evitar
                    reutilizar valores de un folio anterior.
                  </div>
                </div>
              )}

              {nrCotizacion && (
                <div className="nr-subsection mb-3">
                  <div className="nr-subsection-title"><Icon name="info" size={16} /> Desglose oficial NR</div>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Enganche enviado</span>
                        <strong>{formatCurrency(requestedDownPayment)}</strong>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Enganche devuelto por NR</span>
                        <strong>{formatCurrency(nrEngancheReportado)}</strong>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Pago de la unidad</span>
                        <strong>{formatCurrency(nrPagoUnidad)}</strong>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Seguro del vehículo</span>
                        <strong>{formatCurrency(nrPagoSeguro)}</strong>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Seguro de vida mensual</span>
                        <strong>{formatCurrency(nrPagoSeguroVida)}</strong>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Pago mensual de CxA</span>
                        <strong>{formatCurrency(nrPagoCxa)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex gap-2 flex-wrap">
                <button type="button" className="btn btn-outline-primary nr-btn" onClick={handleGuardarNR} disabled={loading.guardar}>
                  <Icon name={loading.guardar ? "loader" : "save"} size={17} className={loading.guardar ? "nr-spin" : ""} />
                  {loading.guardar
                    ? "Guardando"
                    : isCurrentQuoteSaved
                      ? `Guardado · ID ${idCotizacion}`
                      : idCotizacion
                        ? "Guardar cambios en NR"
                        : "Guardar en NR"}
                </button>
                <button type="button" className="btn btn-outline-success nr-btn" onClick={handleGenerarPdfNR} disabled={loading.pdf}>
                  <Icon name={loading.pdf ? "loader" : "file"} size={17} className={loading.pdf ? "nr-spin" : ""} />
                  {loading.pdf ? "Generando PDF" : "Generar PDF NR"}
                </button>
                <button type="button" className="btn btn-success nr-btn" onClick={handleOpenPdf} disabled={!pdfObjectUrl}>
                  <Icon name="file" size={17} /> Ver PDF
                </button>
                <button type="button" className="btn btn-outline-success nr-btn" onClick={handleDownloadPdf} disabled={!pdfObjectUrl}>
                  <Icon name="download" size={17} /> Descargar PDF
                </button>
                <button type="button" className="btn btn-outline-secondary nr-btn ms-md-auto" onClick={() => setRawResponseOpen((prev) => !prev)}>
                  <Icon name="info" size={17} /> {rawResponseOpen ? "Ocultar respuesta técnica" : "Ver respuesta técnica"}
                </button>
              </div>

              {rawResponseOpen && (
                <div className="mt-3">
                  <pre className="nr-json">
                    {JSON.stringify(
                      {
                        cotizaRequest: lastCotizaRequest,
                        cotizacion: nrCotizacion,
                        diagnostico: quoteDiagnostics,
                        segurosAnzen: anzenCotizacion,
                        errorAnzen: anzenError,
                        idCotizacion,
                        cotizacionActualGuardada: isCurrentQuoteSaved,
                        quote,
                      },
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
            </SectionCard>
          </div>

          <div className={`col-xl-4 col-lg-5 ${activeTab !== "cliente" ? "d-none d-lg-block" : ""}`}>
            <aside className="nr-card nr-preview nr-sticky">
              <div className="nr-preview-content">
                <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                  <div>
                    <div className="nr-preview-kicker">Vista del cliente</div>
                    <h3 className="nr-preview-title">Propuesta de financiamiento</h3>
                  </div>
                  <span className="nr-preview-badge">{nrCotizacion ? "Cotizada" : "Borrador"}</span>
                </div>

                <div className="nr-monthly-card mb-3">
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                    <div className="nr-preview-label">Mensualidad estimada</div>
                    <Icon name="sparkles" size={19} />
                  </div>
                  <div className="nr-monthly-value">{selectedResumen.mensualidadNR}</div>
                  <div className="nr-preview-muted mt-2">
                    {nrMensualidad ? `Plazo de ${selectedResumen.plazo}` : "Cotiza con NR para obtener la mensualidad oficial"}
                  </div>
                </div>

                <div className="nr-preview-card mb-2">
                  <div className="nr-preview-label mb-1">Cliente</div>
                  <div className="nr-preview-main">{selectedResumen.cliente}</div>
                  <div className="nr-preview-muted mt-1">{client.telefono || "Teléfono pendiente"}</div>
                  <div className="nr-preview-muted">{client.correo_electronico || "Correo pendiente"}</div>
                </div>

                <div className="nr-preview-card mb-2">
                  <div className="nr-preview-label mb-1">Vehículo seleccionado</div>
                  <div className="nr-preview-main">{selectedResumen.vehiculo}</div>
                  <div className="nr-preview-muted mt-2">{selectedResumen.producto}</div>
                  <div className="nr-preview-muted">Plan: {selectedResumen.plan}</div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <div className="nr-preview-card h-100">
                      <div className="nr-preview-label">Precio</div>
                      <div className="fw-bold mt-1">{selectedResumen.precio}</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="nr-preview-card h-100">
                      <div className="nr-preview-label">Enganche</div>
                      <div className="fw-bold mt-1">{selectedResumen.enganche}</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="nr-preview-card h-100">
                      <div className="nr-preview-label">Plazo</div>
                      <div className="fw-bold mt-1">{selectedResumen.plazo}</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="nr-preview-card h-100">
                      <div className="nr-preview-label">Folio NR</div>
                      <div className="fw-bold mt-1 text-truncate" title={selectedResumen.idCotizacion}>{selectedResumen.idCotizacion}</div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2" style={{ fontSize: 11 }}>
                    <span className="fw-bold">Propuesta completada</span>
                    <span>{progressPercentage}%</span>
                  </div>
                  <div className="nr-preview-progress"><span style={{ width: `${progressPercentage}%` }} /></div>
                </div>

                {!isQuoteReady && (
                  <div className="nr-next-card mb-3">
                    <div className="d-flex align-items-start gap-2">
                      <Icon name="alert" size={18} />
                      <div>
                        <div className="small fw-bold">Siguiente paso</div>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,.72)" }}>
                          Completa {missingRequiredFields[0]?.label || "los datos pendientes"} para continuar.
                        </div>
                      </div>
                    </div>
                    {firstPendingStep && (
                      <button type="button" className="btn btn-sm btn-light nr-btn w-100 mt-2" onClick={() => {
                        setActiveTab("asesor");
                        setTimeout(() => scrollToSection(firstPendingStep.id), 50);
                      }}>
                        Ir al campo pendiente <Icon name="chevron" size={15} />
                      </button>
                    )}
                  </div>
                )}

                <div className="d-grid gap-2">
                  <button type="button" className="btn btn-light nr-btn" onClick={handleOpenPdf} disabled={!pdfObjectUrl}>
                    <Icon name="file" size={17} /> Ver cotización PDF
                  </button>
                  <button type="button" className="btn btn-outline-light nr-btn" onClick={handleDownloadPdf} disabled={!pdfObjectUrl}>
                    <Icon name="download" size={17} /> Descargar PDF
                  </button>
                </div>

                <div className="text-center mt-3" style={{ color: "rgba(255,255,255,.58)", fontSize: 9 }}>
                  La mensualidad oficial se muestra después de cotizar con NR.
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}
